import { Bindable, Persistable, property, remote, Remoteable, Supertype, supertypeClass } from '@haventech/amorphic';
import requestPromise from 'request-promise-native';
import jwt from 'jsonwebtoken';
import _ from 'lodash';
import { StaticContent } from '../templates/static/StaticContent';
import { NYStaticContent } from '../templates/nyStaticContent/NYStaticContent';
import { Header as TemplateHeader } from '../templates/common/Header';
import { Footer } from '../templates/common/Footer';
import { BasicIllustrationStyles } from '../templates/common/Styles';
import { NS_dividends } from '../templates/narrativesummary/dividends/Dividends';
import { Dividend_Static_Info } from '../templates/numericsummary/DividendStaticInfo';
import { NS_info } from '../templates/narrativesummary/info/ClientPolicy';
import { NS_PaymentOptions } from '../templates/narrativesummary/paymentOptions/paymentOptions';
import { IllustrationHtml, IllustrationSummary } from '../templates/illustrationtable/IllustrationSummary';
import { IllustrationBasedOnDiv } from '../templates/illustrationtable/IllustrationBasedOnDiv';
import { CirClaimIllustration } from '../templates/illustrationtable/CIR/CirClaimIllustration';
import {
  IACmain,
  IACoptions,
  IACTextFillOptions,
} from '../templates/narrativesummary/interestAdjustedCost/InterestAdjustedCost';
import { columnHeaderDefinitions } from '../templates/narrativesummary/info/ColumnHeadingDefinitions';
import {
  NumericRTRSummaryTableHtml,
  NumericSummaryTableHtml,
  Style,
  Title,
} from '../templates/illustrationtable/NumericSummary';
import { CoverPage } from '../templates/coverpage/CoverPage';
import { constants } from '../templates/constantsForPdf';
import { Utils } from '../../../../../common/js/Utils';
import { IllustrationService } from '../../IllustrationService';
import { Assumptions } from '../../../../../common/js/static/Assumptions';
import { Product, RiderType, TargetPremiumProducts } from '../../../../../common/js/static/Product';
import { CoverpathWholeLifeQuote } from '../../../quote/coverpath/CoverpathWholeLifeQuote';
import { FetchResult } from 'apollo-fetch';
import { IllustrationRequest } from '../../IllustrationRequest';
import { StateSpecification } from '../templates/common/StateSpecification';
import { CPPolicyRateClassDescriptions, Exchange1035FundingPurpose } from '../../../../js/interfaces/CoverpathTypes';
import { RateClasses } from '../../../../../common/js/static/RateClasses';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { Agent } from '../../../../../common/js/customer/Agent';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { Rider } from '../../../../../common/js/productService/ProductServiceClient';
import { CdmService } from '../../../../../common/js/services/CdmService';
import { AmendmentForm } from '../../../../../coverpath-agent/public/js/components/amendment/AmendmentFormInterface';
import { CoverpathPolicy } from '../../../coverpath_subclasses/CoverpathPolicy';
import { CoverpathCustomer } from '../../../coverpath_subclasses/CoverpathCustomer';
import { IllustrationRequestHelpers } from '../../IllustrationRequestHelpers';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';
import { PolicyRider } from '../../../../../common/js/customer/PolicyRider';
import { Exchange1035 } from '../../../../../common/js/quote/coverpath/Exchange1035';
import { serverOnly } from '../../../../../common/js/helpers';
import { SafeInviteInfo } from '../../../../../common/js/customer/SafeInviteInfo';
import {
  SafeBasePolicy,
  SafeExchange1035,
  SafePolicyRider,
  SafeWholePolicy,
} from '../../../../../common/js/customer/SafePolicyInfo';
import { HtmlPdfUtils } from '../../HtmlPdfUtils';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../../../common/js/logger/LoggerSingleton';

export type Header = {
  authorization: string;
};

export type Body = {
  html: string;
  formOrder?: string;
  formType?: string;
  options?: any;
  footer?: string;
};

type PageInfo = {
  title?: string;
  disclaimer?: string;
  footer?: string;
  offset: number;
};

type IllustrationRider = {
  type: string;
  premium?: number;
  waiver?: number;
  amount?: number;
};

@supertypeClass({ toClient: true, toServer: true })
export class HtmlPdfController extends Bindable(Remoteable(Persistable(Supertype))) {
  protected basicIllustrationData;
  protected basicIllustrationAdditionalData: any = {
    paymentOptions: {},
  };
  protected basicIllustrationDataMidpoint;
  protected supplementalIllustrationData;

  @property({ toClient: false, getType: () => IllustrationService })
  private illustrationService: IllustrationService;
  private agentStateLicenseService: CdmService = new CdmService();

  private readonly cpToMMDividends: Map<string, string> = new Map([
    ['Paid-up Additions', 'Paid-Up Additions'],
    ['Cash', 'Paid in Cash'],
    ['Reduce Premium/Paid-up Additions', 'Reduce Premium, excess to Paid-Up Additions'],
    ['Reduce Premium/Cash', 'Reduce Premium, excess to Cash'],
    ['Dividend Accumulations', 'Accumulate at Interest'],
  ]);
  private readonly dividendOptionMap = {
    PUA: 'Paid-up Additions',
    Cash: 'Cash',
    PRPUA: 'Reduce Premium/Paid-up Additions',
    PRCash: 'Reduce Premium/Cash',
    Accumulate: 'Dividend Accumulations',
  };

  @property({ toClient: false, toServer: false })
  transientRBIInBase64: string;

  @property({ toClient: false, toServer: false })
  transientAmendmentValues: AmendmentForm;

  static new() {
    return new HtmlPdfController().init();
  }

  init() {
    this.illustrationService = new IllustrationService();
    return this;
  }

  getHtmlContent_IllustrationTabularData(illustrationRequest: IllustrationRequest, pageNumber: number): string {
    //TODO : HL-6890 : Once we have real data the number of rows on a page and total number of pages will be calculated here
    const rowsOnPage1 = constants.illustrationRowsOnFirstPage;
    const rowsOnOtherPages = constants.illustrationRowsOnOtherPages;

    const paymentOptions = this.basicIllustrationAdditionalData.paymentOptions;
    let body: string = '';

    if (pageNumber === 1) {
      const startIndex = 0;
      body += `${IllustrationSummary.getSummary(illustrationRequest)}`;
      body += `${IllustrationBasedOnDiv.getTableTitle()}`;
      body += `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnPage1,
        this.basicIllustrationData
      )}`;
    } else {
      const startIndex = pageNumber > 1 ? rowsOnPage1 + rowsOnOtherPages * (pageNumber - 2) : 0;
      body += `${IllustrationBasedOnDiv.getIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnOtherPages,
        this.basicIllustrationData
      )}`;
    }
    const style: string = `${IllustrationSummary.getStyles()}`;
    let hasAlir = false;

    body = body
      .replace(/{{FaceAmount}}/g, Utils.formatNumber(illustrationRequest.faceAmount))
      .replace(/{{BasePremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.basePremium, 2), 2))
      .replace(/{{BasePremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.basePremium, 2), 2))
      .replace(/{{WaiverAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.waiver, 2), 2))
      .replace(/{{WaiverMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.waiver, 2), 2))
      .replace(/{{FlatExtraAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.flatExtra, 2), 2))
      .replace(/{{FlatExtraMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.flatExtra, 2), 2))
      .replace(/{{ModalPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
      .replace(/{{ModalPremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.total, 2), 2))
      .replace(/{{ModalAnnualizedPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
      .replace(
        /{{ModalAnnualizedPremiumMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly.annualTotal, 2), 2)
      );

    if (!!paymentOptions.semiannual && !!paymentOptions.quarterly) {
      body = body
        .replace(
          /{{BasePremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.basePremium, 2), 2)
        )
        .replace(
          /{{BasePremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.basePremium, 2), 2)
        )
        .replace(/{{ModalPremiumSemiAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.semiannual.total, 2), 2))
        .replace(/{{ModalPremiumQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.total, 2), 2))
        .replace(
          /{{ModalAnnualizedPremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.annualTotal, 2), 2)
        )
        .replace(
          /{{ModalAnnualizedPremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.annualTotal, 2), 2)
        );
    }

    if (illustrationRequest.riders) {
      illustrationRequest.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body.replace(/{{GirFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body.replace(/{{RtrFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.CIR) {
          body = body.replace(/{{CirBaseBenefitPool}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.ALIR) {
          hasAlir = true;
          body = body.replace(
            /{{AlirFaceAmount}}/g,
            Utils.formatNumber(
              Utils.round(this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits, 2),
              2
            )
          );
        }
      });
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.annual.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.CIR) {
          body = body
            .replace(/{{CirAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{CirWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
      if (hasAlir) {
        const alirRider = illustrationRequest.riders.find((rider) => rider.type === RiderType.ALIR);
        body = body.replace(/{{AlirAnnual}}/, Utils.formatNumber(Utils.round(alirRider.amount, 2), 2));
        body = body.replace(/{{AlirMonthly}}/, Utils.formatNumber(Utils.round(alirRider.amount / 12, 2), 2));
      }
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.monthly.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.CIR) {
          body = body
            .replace(/{{CirMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{CirWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
    }

    if (illustrationRequest.cashValueTransfer) {
      let cashValueTransferAmount = illustrationRequest.cashValueTransfer;
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        cashValueTransferAmount = illustrationRequest.transferAmount ? illustrationRequest.transferAmount : 0;
      }
      body = body.replace(
        /{{CashValueTransferAmount}}/g,
        Utils.formatCurrency(Utils.round(cashValueTransferAmount, 2), 2)
      );
    }

    return this.getHtmlContent(style, body);
  }

  async getHtmlContent_coverPage(illustrationRequest: IllustrationRequest, revised: boolean): Promise<string> {
    const style = `${CoverPage.Styles}`;
    let subHeaderText = revised ? 'Revised Basic Life Insurance Illustration' : 'Basic Life Insurance Illustration';
    const hasCirRider = !!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR);
    if (hasCirRider) {
      subHeaderText += ' with Chronic Illness Rider (CIR)';
    }
    const body = CoverPage.Body.replace(/{{SUB_HEADER}}/, subHeaderText).replace(
      /{{PRODUCT}}/,
      constants.ProductSelection[illustrationRequest.productCode]
    );
    let coverPageHtml = this.getHtmlContent(style, body);
    coverPageHtml = await this.replaceHtmlContentAgentInfo(illustrationRequest, coverPageHtml);
    coverPageHtml = this.replaceBankSaleDisclosure(illustrationRequest, coverPageHtml);
    return this.replaceHtmlContent(illustrationRequest, coverPageHtml);
  }

  replaceBankSaleDisclosure(illustrationRequest: IllustrationRequest, htmlContent) {
    let bankSaleDisclosure = CoverPage.BankSaleDisclosure;

    // If we need to display agent license on the coverpage,
    // use bank sale disclosure block with alternate styling
    if (StateSpecification.LicenseRequiredStates.includes(illustrationRequest.state)) {
      bankSaleDisclosure = CoverPage.BankSaleDisclosureWithAgentLicense;
    }
    return htmlContent.replace(/{{BankSaleDisclosure}}/, illustrationRequest.bankSale ? bankSaleDisclosure : '');
  }

  getDateInPrettyFormat() {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en', { month: 'long' });
    return formatter.format(date) + ' ' + date.getDate() + ', ' + date.getFullYear();
  }

  getHtmlContent_NS_info(illustrationRequest: IllustrationRequest, isSupplemental?: boolean): string {
    const paymentOptions =
      this.basicIllustrationAdditionalData.paymentOptions[illustrationRequest.paymentFrequency.toLowerCase()];

    const style = `${NS_info.Styles}`;

    let body;
    if (isSupplemental === true) {
      body = `${NS_info.Supplemental_Client_Policy_Premium_Coverage_Info}${StaticContent.INITIAL_INFORMATION_SUPPLEMENTAL}`;
    } else {
      body = `${NS_info.Client_Policy_Premium_Coverage_Info}${StaticContent.INITIAL_INFORMATION}`;
    }

    const product = constants.ProductSelection[illustrationRequest.productCode];
    const productName = constants.ProductNames[illustrationRequest.productCode];
    // If rider is present, this "waiver of premium" display value will include the rider's waiver amount
    let waiverDisplayAmount = paymentOptions.waiver;
    let totalInitialPremium = paymentOptions.total;
    let totalFaceAmount: number = Number(illustrationRequest.faceAmount);

    let flatExtraRow = ``;
    let ratingFlatExtra = ``;
    let waiverOfPremiumRow = ``;
    let girPremiumRow = ``;
    let rtrPremiumRow = ``;
    let rtrFaceAmountRow = ``;
    let alirPremiumRow = ``;
    let alirScheduledCoverageAmount = ``;
    let alirUnscheduledFirstYearLumpSumRow = ``;
    let alir1035FootNoteRow = ``;
    let alirUnscheduledCoverageAmountRow = ``;
    let withCir = ``;
    let withTheCir = ``;
    let cirNSText = ``;
    let cirUnderwritingClassRow = ``;
    let cirPremiumRow = ``;
    let cirBenefitOptionsBlock = ``;
    let infoAboutCir = ``;
    let additionalInfoAboutCir = ``;
    let hasAlirText = ``;
    let cirFootNoteRow = ``;

    let bodyWithFlatAndWop: string = body;
    const rateClass = RateClasses.findByStringCode(illustrationRequest.rateClass);
    const requestRiders = illustrationRequest.riders;

    if (RateClasses.isTableRated(rateClass.code)) {
      ratingFlatExtra = `${NS_info.Table_Rating}`;

      if (illustrationRequest.flatExtra > 0) {
        ratingFlatExtra += `${NS_info.Table_Rating_Flat_Extra}`;
        flatExtraRow = `${NS_info.Flat_Extra}`;
      }

      ratingFlatExtra = ratingFlatExtra.replace(
        /{{TableRatingStringCode}}/g,
        CPPolicyRateClassDescriptions[illustrationRequest.rateClass].shortDescription
      );
    } else {
      if (illustrationRequest.flatExtra > 0) {
        flatExtraRow = `${NS_info.Flat_Extra}`;
        ratingFlatExtra = `${NS_info.Rating_Flat_Extra}`;
      }
    }

    if (illustrationRequest.waiverUnits > 0) {
      waiverOfPremiumRow = `${NS_info.Waiver_Of_Premium}`;
    }

    if (paymentOptions.riders) {
      paymentOptions.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          girPremiumRow = `${NS_info.Gir_Premium}`;
          waiverDisplayAmount += rider.waiver;
        }
        if (rider.type === RiderType.RTR) {
          rtrPremiumRow = `${NS_info.Rtr_Premium}`;
          waiverDisplayAmount += rider.waiver;
        }
        if (rider.type === RiderType.CIR) {
          if (illustrationRequest.waiverUnits > 0) {
            cirPremiumRow = `${NS_info.Cir_Premium_WOP}`;
          } else {
            cirPremiumRow = `${NS_info.Cir_Premium}`;
          }
          waiverDisplayAmount += rider.waiver;
        }
      });
    }

    let alirAmount = 0;
    let rtrAmount = 0;
    let baseBenefitPool = 0;
    let residualFaceAmount = 0;
    let payoutMethod = '';
    let puaIncreaseOption = 'No';
    if (requestRiders) {
      requestRiders.forEach((rider: Rider) => {
        if (rider.type === RiderType.GIR) {
          bodyWithFlatAndWop = bodyWithFlatAndWop.replace(/{{GirFaceAmount}}/g, Utils.formatCurrency(rider.amount));
        }
        if (rider.type === RiderType.RTR) {
          rtrFaceAmountRow = `${NS_info.Rtr_Face_Amount}`;
          totalFaceAmount += Number(rider.amount);
          rtrAmount = rider.amount;
        }
        if (rider.type === RiderType.ALIR) {
          alirScheduledCoverageAmount = `${NS_info.Alir_Scheduled_Coverage_Amount}`;
          alirPremiumRow = `${NS_info.Alir_Premium}`;
          const isAnnual = illustrationRequest.paymentFrequency.toLowerCase() === 'annual';
          alirAmount = isAnnual ? rider.amount : rider.amount / 12;
          totalFaceAmount += this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits;
          hasAlirText = ` and ALIR`;
        }
        if (rider.type === RiderType.CIR) {
          withCir = `${NS_info.Has_Cir}`;
          withTheCir = ' with the CIR';
          cirNSText = `${NS_info.Cir_NS_Text}`;
          cirUnderwritingClassRow = `${NS_info.Cir_UW_Class}`;
          baseBenefitPool = rider.amount;
          residualFaceAmount = illustrationRequest.faceAmount - rider.amount;
          cirBenefitOptionsBlock = `${NS_info.Cir_Benefit_Options}`;
          payoutMethod =
            rider.accelerationPercent === 2
              ? constants.CirPayoutMethodTerm.TwoPercent
              : constants.CirPayoutMethodTerm.FourPercent;
          if (rider.puaAddedToBenefitPool) {
            puaIncreaseOption = rider.monthlyIncreaseOption
              ? constants.CirMonthlyIncreaseOption.IncreasePool
              : constants.CirMonthlyIncreaseOption.IncreasePeriod;
          }
          infoAboutCir = `${StaticContent.INFO_ABOUT_CIR}`;
          additionalInfoAboutCir = `${StaticContent.ADDITIONAL_INFO_ABOUT_CIR}`;
          if (illustrationRequest.waiverUnits > 0) {
            cirFootNoteRow = `${NS_info.Cir_Foot_Note}`;
          }
        }
      });
    }

    let alirUnscheduledAmountYearOne = 0;
    if (illustrationRequest.cashValueTransfer) {
      alirUnscheduledAmountYearOne = this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits;
      alirUnscheduledFirstYearLumpSumRow = `${NS_info.Alir_Unscheduled_First_Year_Lump_Sum}`;
      alir1035FootNoteRow = `${NS_info.Alir_1035_Foot_Note}`;
      alirUnscheduledCoverageAmountRow = `${NS_info.Alir_Unscheduled_Coverage_Amount}`;
      totalFaceAmount += alirUnscheduledAmountYearOne;
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        if (paymentOptions.total < illustrationRequest.cashValueTransfer) {
          illustrationRequest.transferAmount = illustrationRequest.cashValueTransfer - paymentOptions.total;
          totalInitialPremium += illustrationRequest.transferAmount;
        } else {
          //If the cashTranfer value is less than the premium, we wont show the rider
          illustrationRequest.transferAmount = 0;
          totalFaceAmount -= alirUnscheduledAmountYearOne;
          alirUnscheduledAmountYearOne = 0;
        }
      } else {
        totalInitialPremium += illustrationRequest.cashValueTransfer;
      }
    }

    bodyWithFlatAndWop = bodyWithFlatAndWop
      .replace(
        /{{InitialInformationExtraParagraph}}/g,
        StateSpecification.InitialInformationExtraParagraph.includes(illustrationRequest.state)
          ? StaticContent.INITIAL_INFORMATION_EXTRA_PARAGRAPH
          : ''
      )
      .replace(/{{PRODUCT}}/g, product)
      .replace(/{{PRODUCT_NAME}}/g, productName)
      .replace(/{{PRODUCT_PAYABLE_COPY}}/g, constants.PayableCopy[illustrationRequest.productCode])
      .replace(/{{FlatExtraTableRow}}/g, flatExtraRow)
      .replace(/{{WaiverOfPremiumDividendTableRow}}/g, waiverOfPremiumRow)
      .replace(/{{GirPremiumRow}}/g, girPremiumRow)
      .replace(/{{RtrPremiumRow}}/g, rtrPremiumRow)
      .replace(/{{RtrFaceAmountRow}}/g, rtrFaceAmountRow)
      .replace(/{{AlirPremiumRow}}/g, alirPremiumRow)
      .replace(/{{AlirUnscheduledFirstYearLumpSumRow}}/g, alirUnscheduledFirstYearLumpSumRow)
      .replace(/{{Alir1035FootNoteRow}}/g, alir1035FootNoteRow)
      .replace(/{{AlirUnscheduledCoverageAmountRow}}/g, alirUnscheduledCoverageAmountRow)
      .replace(/{{AlirScheduledCoverageAmount}}/g, alirScheduledCoverageAmount)
      .replace(/{{BasePolicyPremium}}/, Utils.formatCurrency(paymentOptions.basePremium))
      .replace(/{{WaiverOfPremium}}/, Utils.formatCurrency(waiverDisplayAmount))
      .replace(/{{FlatExtraPremium}}/, Utils.formatCurrency(paymentOptions.flatExtra))
      .replace(/{{TotalInitialPremium}}/, Utils.formatCurrency(totalInitialPremium))
      .replace(/{{BasePolicyFaceAmount}}/, Utils.formatCurrency(illustrationRequest.faceAmount))
      .replace(/{{TotalInitialDeathBenefit}}/, Utils.formatCurrency(totalFaceAmount))
      .replace(/{{Rating_Flat_Extra}}/g, ratingFlatExtra)
      .replace(/{{WithCir}}/g, withCir)
      .replace(/{{CirNSText}}/g, cirNSText)
      .replace(/{{CirUWClassRow}}/g, cirUnderwritingClassRow)
      .replace(/{{CirPremiumRow}}/g, cirPremiumRow)
      .replace(/{{CirBenefitOptionsBlock}}/g, cirBenefitOptionsBlock)
      .replace(/{{InfoAboutCir}}/g, infoAboutCir)
      .replace(/{{AdditionalInfoAboutCir}}/g, additionalInfoAboutCir.replace(/{{PRODUCT}}/g, product))
      .replace(/{{HasAlirText}}/g, hasAlirText)
      .replace(/{{CirFootNoteRow}}/g, cirFootNoteRow)
      .replace(/{{WithTheCir}}/g, withTheCir);

    if (illustrationRequest.cashValueTransfer) {
      let cashValueTransferAmount = illustrationRequest.cashValueTransfer;
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        cashValueTransferAmount = illustrationRequest.transferAmount;
      }
      bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
        /{{CashValueTransferAmount}}/g,
        Utils.formatCurrency(Utils.round(cashValueTransferAmount, 2), 2)
      );
      const expectedArrival = illustrationRequest.expectedArrival ? illustrationRequest.expectedArrival : 0;
      bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
        /{{TransferAmountArrivalTime}}/g,
        expectedArrival.toString() + (expectedArrival === 1 ? ' month' : ' months')
      );
      bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
        /{{TransferAmountMaxArrivalTime}}/g,
        (expectedArrival === 0 ? '1' : expectedArrival.toString()) + (expectedArrival === 1 ? ' month' : ' months')
      );
      bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
        /{{AlirUnscheduledAmount}}/g,
        Utils.formatCurrency(Utils.round(alirUnscheduledAmountYearOne, 2), 2)
      );
    }

    if (rtrAmount) {
      bodyWithFlatAndWop = bodyWithFlatAndWop.replace(/{{RtrFaceAmount}}/g, Utils.formatCurrency(rtrAmount));
    }
    if (alirAmount) {
      bodyWithFlatAndWop = bodyWithFlatAndWop
        .replace(
          /{{AlirFaceAmount}}/g,
          Utils.formatCurrency(this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits)
        )
        .replace(/{{AlirPremium}}/g, Utils.formatCurrency(alirAmount));
    }
    if (baseBenefitPool) {
      bodyWithFlatAndWop = bodyWithFlatAndWop
        .replace(/{{BaseBenefitPool}}/g, Utils.formatCurrency(baseBenefitPool))
        .replace(/{{ResidualFaceAmount}}/g, Utils.formatCurrency(residualFaceAmount))
        .replace(/{{PayoutMethod}}/g, payoutMethod)
        .replace(/{{BasePUAAvailable}}/g, puaIncreaseOption);
    }
    if (paymentOptions.riders) {
      paymentOptions.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
            /{{GirPremium}}/g,
            Utils.formatCurrency(Utils.round(rider.premium, 2), 2)
          );
        }
        if (rider.type === RiderType.RTR) {
          bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
            /{{RtrPremium}}/g,
            Utils.formatCurrency(Utils.round(rider.premium, 2), 2)
          );
        }
        if (rider.type === RiderType.CIR) {
          bodyWithFlatAndWop = bodyWithFlatAndWop.replace(
            /{{CirPremium}}/g,
            Utils.formatCurrency(Utils.round(rider.premium, 2), 2)
          );
        }
      });
    }

    const htmlContent = this.getHtmlContent(style, bodyWithFlatAndWop);
    return this.replaceHtmlContent(illustrationRequest, htmlContent);
  }

  private findRiderByType(riders: Array<Rider | IllustrationRider>, riderType: string): IllustrationRider {
    return riders.find((rider) => rider.type === riderType);
  }

  private getHtmlContent(style: string, body: string) {
    return `
        <head>
            <style>
                ${style}
            </style>
        </head>
        <body>
            <div class="page-wrapper">
                ${body}
            </div>
        </body>
        `;
  }

  private async getFilledFooter(illustrationRequest: IllustrationRequest, last: boolean = false) {
    let body: string = Footer.getFooterBody(last);
    body = this.replaceHtmlContent(illustrationRequest, body);
    body = await this.replaceHtmlContentAgentInfo(illustrationRequest, body);

    const mecLimit =
      illustrationRequest.mecStatus === 'Yes' ? 0 : this.basicIllustrationData.illustration.payload.policy.mecLimit;

    return body
      .replace(/{{MECLimit}}/g, Utils.formatCurrency(mecLimit))
      .replace(/{{Version}}/g, constants.CPVersion)
      .replace(/{{State}}/g, illustrationRequest.state);
  }

  async replaceHtmlContentAgentInfo(illustrationRequest: IllustrationRequest, htmlContent) {
    const agentLicenseNumber = await this.getStateLicenseContent(illustrationRequest);

    return htmlContent
      .replace(/{{AgentFirstName}}/g, illustrationRequest.agent.firstName)
      .replace(/{{AgentLastName}}/g, illustrationRequest.agent.lastName)
      .replace(/{{AgentAddressLine1}}/g, illustrationRequest.agent.address.street)
      .replace(/{{AgentCity}}/g, illustrationRequest.agent.address.city)
      .replace(/{{AgentState}}/g, illustrationRequest.agent.address.state)
      .replace(/{{AgentZipCode}}/g, illustrationRequest.agent.address.zip)
      .replace(/{{AgentLicenseNumber}}/g, agentLicenseNumber)
      .replace(/{{AgentPhoneNumber}}/g, this.getAgentPhone(illustrationRequest));
  }

  async getStateLicenseContent(illustrationRequest: IllustrationRequest): Promise<string> {
    if (
      StateSpecification.LicenseRequiredStates.includes(illustrationRequest.state) &&
      !illustrationRequest.isWholesaler
    ) {
      let { licenseNumber } = illustrationRequest;
      if (!licenseNumber) {
        licenseNumber = await this.fetchStateLicense(illustrationRequest.agent.producerId, illustrationRequest.state);
      }
      return `<p class="p3 ft3">Agent License Number: ${licenseNumber} </p>`;
    }
    return '';
  }

  private async fetchStateLicense(producerId: string, state: string): Promise<string> {
    const licenses = await this.agentStateLicenseService.getStateLicenses(producerId);

    const stateLicense = licenses.find((license) => license.state === state);
    if (stateLicense) {
      return stateLicense.licenseNumber;
    }
  }

  getAgentPhone(illustrationRequest: IllustrationRequest): string {
    if (StateSpecification.PhoneNumberRequiredStates.includes(illustrationRequest.state)) {
      const agentPhone = illustrationRequest.agent.phone;
      if (agentPhone) {
        return `<p class="p3 ft3">Agent Telephone Number: ${agentPhone} </p>`;
      }
    }
    return '';
  }

  replaceHtmlContent(illustrationRequest: IllustrationRequest, htmlContent) {
    //check if either a)mecStatus has been set already in illustrationRequest determined by Exchange1035 field on quote page
    //or b)mecYear returned from product illustration call is > 0
    const mecStatus =
      illustrationRequest.mecStatus === 'Yes' || this.basicIllustrationData.illustration.payload.policy.mecYear > 0
        ? 'Yes'
        : 'No';
    let underwritingClass = CPPolicyRateClassDescriptions[illustrationRequest.rateClass].tobaccoDescription;
    const cirUnderwritingClass = this.getCirUnderwritingRateClass(underwritingClass);

    const rateClass = RateClasses.findByStringCode(illustrationRequest.rateClass);
    const rateClassDescription = CPPolicyRateClassDescriptions[illustrationRequest.rateClass];
    let underWritingClassForFooter = RateClasses.isTableRated(rateClass.code)
      ? rateClassDescription.longDescription
      : rateClassDescription.tobaccoDescription;

    if (this.isInsuredJuvenile(illustrationRequest)) {
      underwritingClass = 'Juvenile';
      underWritingClassForFooter = 'Juvenile';
    }

    return (
      htmlContent
        .replace(
          /{{LowerDividendWarning}}/,
          illustrationRequest.dividendInterestAdjustment > 0 ? CoverPage.LowerDividendProductionLabel : ''
        )
        .replace(/{{ClientFirstName}}/g, illustrationRequest.customerPerson.firstName)
        .replace(/{{ClientLastName}}/g, illustrationRequest.customerPerson.lastName)
        .replace(/{{CurrentDate}}/g, this.getDateInPrettyFormat())
        .replace(/{{CopyrightYear}}/g, CoverpathUtils.getCopyrightYear())
        .replace(/{{TemporaryFlatExtraYears}}/g, constants.TemporaryFlatExtraYears)
        .replace(/{{GenericPolicyName}}/g, this.getGenericPolicyName(illustrationRequest.productCode))
        .replace(/{{PolicyFormNumber}}/g, this.getPolicyFormNumber(illustrationRequest))
        .replace(/{{MECStatus}}/g, mecStatus)
        .replace(
          /{{ClientGender}}/g,
          CoverpathUtils.getGenderInPrettyFormat(illustrationRequest.gender, illustrationRequest.state)
        )
        .replace(
          /{{ClientAge}}/g,
          CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)
        )
        .replace(/{{CRNValue}}/g, this.amorphic.config.nconf.get('wholeLifeBasicIllustrationCRN'))
        //we use the insuranceQuote datamodel for the descriptions, but look up the value as specified in the request
        .replace(/{{UnderwritingClass}}/g, underwritingClass)
        .replace(/{{CirUnderwritingClass}}/g, cirUnderwritingClass)
        .replace(
          /{{DividendOption}}/g,
          this.cpToMMDividends.get(
            new CoverpathWholeLifeQuote().__descriptions__('dividend')[illustrationRequest.dividendOption]
          )
        )
        .replace(/{{PremiumPaymentMode}}/g, illustrationRequest.paymentFrequency)
        .replace(/{{UnderwritingClassForFooter}}/g, underWritingClassForFooter)
        .replace(
          /{{TotalTargetPremium}}/g,
          TargetPremiumProducts.includes(illustrationRequest.productCode)
            ? this.getTotalTargetPremiumString(
                this.basicIllustrationData.illustration.payload.policy.totalAnnualTargetPremium
              )
            : ''
        )
    );
  }

  private getTotalTargetPremiumString(totalTargetPremium: number) {
    return `TGT${Math.floor(Number(totalTargetPremium)).toString()}`;
  }

  private getPolicyFormNumber({ state }) {
    switch (state) {
      case 'FL':
        return constants.PolicyFormNumberForFlorida;
      case 'CA':
        return constants.PolicyFormNumberForCalifornia;
      case 'DE':
        return constants.PolicyFormNumberForDeleware;
      case 'ND':
        return constants.PolicyFormNumberForNorthDakota;
      case 'SD':
        return constants.PolicyFormNumberForSouthDakota;
      case 'NY':
        return constants.PolicyFormNumberForNewYork;
      default:
        return constants.PolicyFormNumber;
    }
  }

  // Interest Adjusted Cost (IAC) Comparison Index
  getHTMLContent_NS_IAC(illustrationRequest: IllustrationRequest, isSupplemental?: boolean): string {
    const startDate = this.basicIllustrationData.illustration.payload.policy.segments[0].startDate;
    const iacData = this.basicIllustrationData.illustration.payload.policy.costIndexList;
    const loanInterestRate = this.basicIllustrationData.illustration.payload.policy.loanInterestRate;

    // Get the right config for the state
    const IACConfig: IACTextFillOptions = StateSpecification.GetContentByState(
      illustrationRequest.state,
      StateSpecification.IACTextFillOptions,
      IACmain.TextFillOptions
    ) as IACTextFillOptions;

    // Begin creating body
    let body: string = isSupplemental === true ? IACmain.supplementalBody : IACmain.body;
    body += IACConfig.body;

    // Always replace first row, Surrender Cost Row
    body = body
      .replace(/{{LI_SCI_PY_10}}/, Utils.formatCurrency(iacData[9].surrenderCostIndex))
      .replace(/{{LI_SCI_PY_20}}/, Utils.formatCurrency(iacData[19].surrenderCostIndex));

    // Optionally add and fill in second row, Net Payment Cost Row
    const shouldIncludeNPCR: boolean = StateSpecification.IACNetPaymentCostRow.includes(illustrationRequest.state);
    body = body.replace(/{{NetPaymentCostRow}}/, shouldIncludeNPCR ? IACmain.NetPaymentCostRow : '');
    if (shouldIncludeNPCR) {
      body = body
        .replace(/{{LI_NPCI_PY_10}}/, Utils.formatCurrency(iacData[9].netPaymentIndex))
        .replace(/{{LI_NPCI_PY_20}}/, Utils.formatCurrency(iacData[19].netPaymentIndex));
    }

    // Optionally add and fill in third row, Equivalent Level Row
    const shouldIncludeEQR: boolean = StateSpecification.IACEquivalentLevelRow.includes(illustrationRequest.state);
    body = body.replace(/{{EquivalentLevelRow}}/, shouldIncludeEQR ? IACmain.EquivalentLevelRow : '');
    if (shouldIncludeEQR) {
      body = body
        .replace(/{{ELAD5_PY_10}}/, Utils.formatCurrency(iacData[9].levelDividend))
        .replace(/{{ELAD5_PY_20}}/, Utils.formatCurrency(iacData[19].levelDividend));
    }

    // Loop through the rest of the properties and replace accordingly
    _.forIn(_.omit(IACConfig, ['body']), (value, key) => {
      const replacementText = '{{' + key + '}}';
      body = body.replace(replacementText, value);
    });

    // Optionally add and fill in the 5 year column
    const shouldIncludeYear5Col = StateSpecification.IACYear5Column.includes(illustrationRequest.state);
    const col5YearFill = shouldIncludeYear5Col ? IACmain.Col5YearOptions.Year5 : IACmain.Col5YearOptions.None;

    _.forIn(col5YearFill, (value, key) => {
      const replacementText = '{{' + key + '}}';
      body = body.replace(replacementText, value);
    });

    if (shouldIncludeYear5Col) {
      body = body
        .replace(/{{LI_SCI_PY_5}}/, Utils.formatCurrency(iacData[4].surrenderCostIndex))
        .replace(/{{LI_NPCI_PY_5}}/, Utils.formatCurrency(iacData[4].netPaymentIndex))
        .replace(/{{ELAD5_PY_5}}/, Utils.formatCurrency(iacData[4].levelDividend));
    }

    // Add style
    const style: string = IACmain.styles;

    //Use Cash Value as a flag for 1035 Exchange.
    let is1035ExchangeToALIR = illustrationRequest.cashValueTransfer > 0;
    let is1035LowTransferAmount = false;
    //If the cashTranfer value is less than the premium, we wont show the rider
    if (illustrationRequest.cashValueTransfer) {
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        is1035ExchangeToALIR = true;
      } else if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        if (illustrationRequest.transferAmount > 0) {
          is1035ExchangeToALIR = true;
        } else {
          is1035ExchangeToALIR = false;
          is1035LowTransferAmount = true;
        }
      }
    }

    // PUA = Paid-up Additions
    // PRPUA = Reduce Premium / Paid-up Additions
    // Accumulate = Divident Accumulations
    // Cash = Cash
    // PRCash = Reduced Premium / Cash
    switch (illustrationRequest.dividendOption) {
      case 'PUA':
      case 'PRPUA':
        body += IACoptions.HEADER_AND_FULLY_ALLOCATED;

        if (is1035ExchangeToALIR) {
          body += IACoptions.MEC_STATUS;
          body += IACoptions.UNSCHEDULED_ALIR_DIVIDEND;
          body += IACoptions.UNSCHEDULED_ALIR_PAYMENT;
        } else if (is1035LowTransferAmount) {
          //thrifty phil use case
          body += IACoptions.MEC_STATUS;
        }

        break;
      case 'Accumulate':
      case 'Cash':
      case 'PRCash':
        body += IACoptions.HEADER_AND_FULLY_ALLOCATED;

        if (is1035ExchangeToALIR) {
          body += IACoptions.MEC_STATUS;
          body += IACoptions.UNSCHEDULED_ALIR_DIVIDEND;
          body += IACoptions.UNSCHEDULED_ALIR_PAYMENT;
        } else if (is1035LowTransferAmount) {
          //thrifty phil use case
          body += IACoptions.MEC_STATUS;
        }

        body += IACoptions.ANNUAL_NET_OUTLAY;

        break;
    }

    // Optionally add extra paragraph based on loan type
    if (StateSpecification.IACAdditionalInfoParagraph.includes(illustrationRequest.state)) {
      body +=
        illustrationRequest.loanRateType.toLowerCase() === 'variable'
          ? IACmain.AdditionalInfo.Variable
          : IACmain.AdditionalInfo.Fixed;
    }

    const expectedArrival = illustrationRequest.expectedArrival ? illustrationRequest.expectedArrival : 0;
    const transferAmount =
      illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR
        ? illustrationRequest.transferAmount
        : illustrationRequest.cashValueTransfer;
    body = body
      .replace(/{{TAX_BRACKET}}/g, Utils.round(illustrationRequest.taxRate * 100, 0).toString())
      .replace(/{{RATE}}/g, (loanInterestRate * 100).toFixed(2))
      .replace(/{{STATE}}/g, Assumptions.stateValues[illustrationRequest.state])
      .replace(/{{DATE}}/g, Utils.tzDate(new Date(startDate)).toMonthDY())
      .replace(/{{COST_BASIS}}/g, Utils.formatCurrency(illustrationRequest.costBasis, 2))
      .replace(/{{CASH_VALUE_TRANSFER}}/g, Utils.formatCurrency(illustrationRequest.cashValueTransfer, 2))
      .replace(/{{TRANSFER_AMOUNT}}/g, Utils.formatCurrency(transferAmount, 2))
      .replace(/{{EXPECTED_ARRIVAL}}/g, expectedArrival.toString() + (expectedArrival === 1 ? ' month' : ' months'))
      .replace(
        /{{VARIABLE_RATE_MESSAGE}}/,
        illustrationRequest.loanRateType.toLowerCase() === 'variable'
          ? ' This rate may change on each policy anniversary date.'
          : ''
      );

    return this.getHtmlContent(style, body);
  }

  getHtmlContent_NS_PaymentOptions(illustrationRequest: IllustrationRequest): string {
    const paymentOptions = this.basicIllustrationAdditionalData.paymentOptions;
    let paymentOptionsHTML = StateSpecification.NoPremiumPaymentOption.includes(illustrationRequest.state)
      ? ''
      : NS_PaymentOptions.content
          .replace(/{{AnnualPremium}}/g, Utils.formatCurrency(paymentOptions.annual.total))
          .replace(/{{MonthlyPremium}}/, Utils.formatCurrency(paymentOptions.monthly.total))
          .replace(/{{TotalMonthlyPremium}}/, Utils.formatCurrency(paymentOptions.monthly.annualTotal))
          .replace(/{{AdditionalChargeMonthly}}/, Utils.formatCurrency(paymentOptions.monthly.additionalCharge))
          .replace(/{{RATE}}/g, '9.5%'); // Rate is 9.5%
    paymentOptionsHTML += StaticContent.CONSIDER_ADDITONAL_COVERAGE.replace(
      /{{PRODUCT}}/g,
      constants.ProductSelection[illustrationRequest.productCode]
    );
    return this.getHtmlContent(NS_PaymentOptions.style, paymentOptionsHTML);
  }

  calculateBasePremium(benefit, paymentFrequency: ProductApiTypes.PaymentFrequency): number {
    const baseKey = paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualBasePremium' : 'basePremium';
    const feeKey = paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualFee' : 'fee';
    const tableRatingPremiumKey =
      paymentFrequency === ProductApiTypes.PaymentFrequency.Annual ? 'annualTableRatingPremium' : 'tableRatingPremium';

    return benefit[baseKey] + benefit[feeKey] + benefit[tableRatingPremiumKey];
  }

  @serverOnly()
  getPaymentOptions(premiumData, illustrationRequest: IllustrationRequest) {
    // In the Illustration service request, the payment frequency of the first policy to be monthly and annual to the second one.
    const [monthly, annual] = premiumData;
    const paymentOptions = {
      annual: this.getPaymentOptionValuesFromSegments(annual.segments, ProductApiTypes.PaymentFrequency.Annual),
      monthly: this.getPaymentOptionValuesFromSegments(monthly.segments, ProductApiTypes.PaymentFrequency.Monthly),
    };

    // ALIR premium values are in the request, not the PA response
    const requestedRiders = illustrationRequest.riders;
    const requestedAlirRider = requestedRiders ? requestedRiders.find((rider) => rider.type === RiderType.ALIR) : null;
    if (requestedAlirRider) {
      paymentOptions.annual.total += requestedAlirRider.amount; // amount always annual
      paymentOptions.monthly.total += requestedAlirRider.amount / 12;
    }

    paymentOptions.monthly.annualTotal = paymentOptions.monthly.total * 12;
    paymentOptions.monthly.additionalCharge = paymentOptions.monthly.annualTotal - paymentOptions.annual.total;

    return paymentOptions;
  }

  @serverOnly()
  getPaymentOptionValuesFromSegments(segments, paymentFrequency) {
    let paymentOption;

    if (paymentFrequency === ProductApiTypes.PaymentFrequency.Annual) {
      paymentOption = {
        basePremium: 0,
        premium: 0,
        waiver: 0,
        flatExtra: 0,
        total: 0,
        riders: [],
      };
    } else if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
      paymentOption = {
        basePremium: 0,
        premium: 0,
        waiver: 0,
        flatExtra: 0,
        total: 0,
        annualPremium: 0,
        annualWaiver: 0,
        annualTotal: 0,
        additionalCharge: 0,
        riders: [],
      };
    } else {
      //throw error?
      const error = {
        isHumanRelated: false,
        data: {
          paymentFrequency: paymentFrequency,
        },
      };
      LoggerSingleton.getInstance().error({
        module: 'HtmlPdfController',
        function: 'getPaymentOptionValuesFromSegments',

        error: error,
        category: Category.request,
      });
    }
    segments.forEach((segment) => {
      const riderType: string = segment.riderType;
      const benefits = segment.benefits;
      const rider: IllustrationRider = {
        type: riderType,
        premium: 0,
        waiver: 0,
      };

      benefits.forEach((benefit) => {
        switch (benefit.code) {
          case 'Death':
            if (riderType === RiderType.RTR) {
              //death benefit for RTR segment
              rider.premium = benefit.basePremium + benefit.tableRatingPremium;
            } else {
              //death benefit for base policy segment

              paymentOption.basePremium =
                paymentFrequency === ProductApiTypes.PaymentFrequency.Annual
                  ? this.calculateBasePremium(benefit, ProductApiTypes.PaymentFrequency.Annual)
                  : this.calculateBasePremium(benefit, ProductApiTypes.PaymentFrequency.Monthly);
              if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
                //set annual premium for montly payment option
                paymentOption.annualPremium = paymentOption.premium * 12;
              }
            }

            paymentOption.premium = benefit.premium;
            //update total and flat extras for all segment types
            paymentOption.total += paymentOption.premium;
            benefit.flatExtras.forEach((flatExtra) => {
              paymentOption.flatExtra += flatExtra.premium;
            });
            break;
          case RiderType.GIR: {
            //GIR benefit for GIR segment
            // update annual.premium? flat extras?
            paymentOption.total += benefit.premium;
            rider.premium = benefit.basePremium;
            break;
          }
          case RiderType.CIR: {
            //CIR benefit for CIR segment
            paymentOption.total += benefit.premium;
            rider.premium = benefit.basePremium;
            break;
          }
          case 'Waiver':
            if (riderType === RiderType.GIR || riderType === RiderType.RTR || riderType === RiderType.CIR) {
              //waiver benefit for GIR or RTR segment
              rider.waiver = benefit.premium;
            } else {
              //waiver benefit for base policy segment
              paymentOption.waiver = benefit.premium;
              if (paymentFrequency === ProductApiTypes.PaymentFrequency.Monthly) {
                //set annual waiver only for monthly payment option
                paymentOption.annualWaiver = paymentOption.waiver * 12;
              }
            }
            //update total to include waiver benefit for all segment types
            paymentOption.total += benefit.premium;

            break;
        }
      });
      if (riderType) {
        //if current segment is a rider, add rider to array
        paymentOption.riders.push(rider);
      }
    });

    return paymentOption;
  }

  @serverOnly()
  getHtmlContent_NS_ColumnHeadingDefinitions(illustrationRequest: IllustrationRequest): string {
    const htmlColumns = [];
    htmlColumns.push(columnHeaderDefinitions.AGE_END_YEAR);

    const hasCirRider = !!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR);
    let cirAddPaidupAdditionsEndYear = ``;
    let cirAddTotalCashValueEndYear = ``;
    let cirAddTotalDeathBenefitEndYear = ``;
    if (hasCirRider) {
      cirAddPaidupAdditionsEndYear = `${columnHeaderDefinitions.CIR_ADD_PAIDUP_ADDITIONS_END_YEAR}`;
      cirAddTotalCashValueEndYear = `${columnHeaderDefinitions.CIR_ADD_TOTAL_CASH_VALUE_END_YEAR}`;
      cirAddTotalDeathBenefitEndYear = `${columnHeaderDefinitions.CIR_ADD_TOTAL_DEATH_BENEFIT_END_YEAR}`;

      htmlColumns.push(columnHeaderDefinitions.CIR_BENEFIT_MONTHS_REMAINING_END_YEAR);
      htmlColumns.push(columnHeaderDefinitions.CIR_BENEFIT_PAYMENT);
      htmlColumns.push(columnHeaderDefinitions.CIR_BENEFIT_POOL_END_YEAR);
      htmlColumns.push(columnHeaderDefinitions.CIR_PREMIUM);
      htmlColumns.push(columnHeaderDefinitions.CIR_NET_CASH_VALUE_END_YEAR);
      htmlColumns.push(columnHeaderDefinitions.CIR_NET_DEATH_BENEFIT_END_YEAR);
      htmlColumns.push(columnHeaderDefinitions.POLICY_PREMIUM);
      htmlColumns.push(columnHeaderDefinitions.TOTAL_OUTSTANDING_POLICY_DEBT_END_YEAR);
    }

    switch (illustrationRequest.dividendOption) {
      case 'PUA':
        if (!hasCirRider) {
          htmlColumns.push(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR);
        }
        htmlColumns.push(columnHeaderDefinitions.CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CONTRACT_PREMIUM);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.PAIDUP_ADDITIONS_END_YEAR);
        break;
      case 'Cash':
        htmlColumns.push(columnHeaderDefinitions.ANNUAL_NET_BEG_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CONTRACT_PREMIUM);
        htmlColumns.push(columnHeaderDefinitions.DIVIDEND_PAID_CASH_BEG_YAER);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR);
        break;
      case 'PRPUA':
        if (!hasCirRider) {
          htmlColumns.push(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR);
        }
        htmlColumns.push(columnHeaderDefinitions.CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CONTRACT_PREMIUM);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.NET_PREMIUM_BEG_YEAR);
        htmlColumns.push(columnHeaderDefinitions.PAIDUP_ADDITIONS_END_YEAR);
        break;
      case 'PRCash':
        if (!hasCirRider) {
          htmlColumns.push(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR);
        }
        htmlColumns.push(columnHeaderDefinitions.ANNUAL_NET_BEG_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CONTRACT_PREMIUM);
        htmlColumns.push(columnHeaderDefinitions.DIVIDEND_PAID_CASH_BEG_YAER);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.NET_PREMIUM_BEG_YEAR);
        htmlColumns.push(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR);
        break;
      case 'Accumulate':
        htmlColumns.push(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.ANNUAL_NET_BEG_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CASH_VALUE_ACCUMS_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.CONTRACT_PREMIUM);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR);
        htmlColumns.push(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR);
        break;
    }
    htmlColumns.push(columnHeaderDefinitions.TOTAL_CASH_VALUE_END_YEAR);
    htmlColumns.push(columnHeaderDefinitions.TOTAL_DEATH_BENEFIT_END_YEAR);
    htmlColumns.push(columnHeaderDefinitions.TOTAL_PAIDUP_INS_END_YEAR);
    htmlColumns.push(columnHeaderDefinitions.YEAR);

    let html = `${columnHeaderDefinitions.title}`;

    // sort html columns in alpha order
    const sortedHtmlColumns = _.orderBy(htmlColumns, [(col) => col.title.toLowerCase()], ['title']);
    _.forEach(sortedHtmlColumns, (col) => {
      html += `${columnHeaderDefinitions.parse(col)}\n`;
    });

    html = html
      .replace(/{{CirAddPaidupAdditionsEndYear}}/g, cirAddPaidupAdditionsEndYear)
      .replace(/{{CirAddTotalCashValueEndYear}}/g, cirAddTotalCashValueEndYear)
      .replace(/{{CirAddTotalDeathBenefitEndYear}}/g, cirAddTotalDeathBenefitEndYear);

    return html;
  }

  getHtmlContent_NS_dividends(illustrationRequest: IllustrationRequest): string {
    const startDate = this.basicIllustrationData.illustration.payload.policy.segments[0].startDate;
    const loanInterestRate = this.basicIllustrationData.illustration.payload.policy.loanInterestRate;
    const dividendSection = NS_dividends.DividendsMap[illustrationRequest.dividendOption];
    const style = '';
    const requestRiders = illustrationRequest.riders;
    let requestHasGir = false;
    let requestHasRtr = false;
    let requestHasAlir = false;
    let requestHasCir = false;
    const isNYIllustration: boolean = illustrationRequest.state === 'NY';
    let hasCirRiderAndPUA = false;

    if (requestRiders && requestRiders.length > 0) {
      requestRiders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          requestHasGir = true;
        }
        if (rider.type === RiderType.RTR) {
          requestHasRtr = true;
        }
        if (rider.type === RiderType.ALIR) {
          requestHasAlir = true;
        }
        if (rider.type === RiderType.CIR) {
          requestHasCir = true;
          if (rider.puaAddedToBenefitPool) {
            hasCirRiderAndPUA = true;
          }
        }
      });
    }

    let is1035ExchangeToALIR = illustrationRequest.cashValueTransfer > 0;
    //If the cashTranfer value is less than the premium, we wont show the rider
    if (illustrationRequest.cashValueTransfer) {
      requestHasAlir = true;
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        is1035ExchangeToALIR = true;
      } else if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        if (illustrationRequest.transferAmount > 0) {
          is1035ExchangeToALIR = true;
        } else {
          is1035ExchangeToALIR = false;
        }
      }
    }

    let body = `${
      illustrationRequest.dividendInterestAdjustment > 0
        ? StaticContent.INFO_ABOUT_DIVIDENDS_LOWER_DIVIDEND
        : StaticContent.INFO_ABOUT_DIVIDENDS
    }
            ${dividendSection}
            ${is1035ExchangeToALIR ? StaticContent.INFO_ABOUT_DIVIDENDS_1035 : ''}
            ${StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS}
            ${illustrationRequest.waiverUnits > 0 ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_WP : ''}
            ${requestHasGir ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_GIR : ''}
            ${requestHasRtr && !isNYIllustration ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_RTR : ''}
            ${requestHasRtr && isNYIllustration ? NYStaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_RTR : ''}
            ${requestHasAlir ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_ALIR : ''}`;

    body += StaticContent.IMPORTANT_TAX_INFORMATION_HEADER;

    //check if either a)mecStatus has been set already in illustrationRequest determined by Exchange1035 field on quote page
    //or b)mecYear returned from product illustration call is > 0
    let mecStatus;
    let mecYear;
    if (illustrationRequest.mecStatus === 'Yes') {
      mecStatus = true;
      mecYear = 1;
    } else if (this.basicIllustrationData.illustration.payload.policy.mecYear > 0) {
      mecStatus = true;
      mecYear = this.basicIllustrationData.illustration.payload.policy.mecYear;
    } else {
      mecStatus = false;
    }
    if (mecStatus) {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_MEC;
      body = body.replace(/{{mecYear}}/g, mecYear);
    } else {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_NO_MEC;
    }

    if (requestHasGir) {
      const gir = this.findRiderByType(requestRiders, RiderType.GIR);
      body = body.replace(/{{GirFaceAmount}}/g, Utils.formatCurrency(gir.amount));
    }

    if (requestHasRtr) {
      if (illustrationRequest.dob) {
        body = body.replace(
          /{{RTR_END_YEAR}}/g,
          (95 - CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)).toString()
        );
      }
    }

    if (requestHasAlir) {
      body = body.replace(/{{ALIR_PERCENTAGE}}/g, '7.5%');
    }

    body += StaticContent.IMPORTANT_TAX_INFORMATION_FOOTER;

    let cirTaxInfo = ``;
    let additionalCirTaxInfo = ``;
    let cirPuaInfo = ``;
    if (requestHasCir) {
      cirTaxInfo = StaticContent.CIR_TAX_INFO;
      additionalCirTaxInfo = StaticContent.ADDITIONAL_CIR_TAX_INFO;

      if (hasCirRiderAndPUA) {
        cirPuaInfo = StaticContent.CIR_PUA_INFO;
      }
    }

    body += StaticContent.IMPORTANT_POLICY_LOANS_INFORMATION_HEADER;

    body += StaticContent.IMPORTANT_POLICY_INTEREST_RATE;

    body = body
      .replace(/{{PRODUCT}}/g, constants.ProductSelection[illustrationRequest.productCode])
      .replace(/{{YEAR}}/, CoverpathUtils.getCurrentRateYear().toString())
      .replace(
        /{{DIVIDEND_ADJUSTMENT}}/,
        CoverpathUtils.decimalToPercent(illustrationRequest.dividendInterestAdjustment).toString()
      )
      .replace(
        /{{DIVIDEND_ADJUSTMENT}}/,
        CoverpathUtils.decimalToPercent(illustrationRequest.dividendInterestAdjustment).toString()
      )
      .replace(/{{RATE_ELECTION}}/, illustrationRequest.loanRateType === 'Fixed' ? 'a fixed' : 'an adjustable')
      .replace(/{{RATE_FIXED}}/, illustrationRequest.loanRateType === 'Fixed' ? 'Loans affect dividends.' : '')
      .replace(/{{CirTaxInfo}}/g, cirTaxInfo)
      .replace(/{{AdditionalCirTaxInfo}}/g, additionalCirTaxInfo)
      .replace(/{{HAS_CIR_PUA}}/g, cirPuaInfo)
      .replace(/{{LOAN_INTEREST_RATE}}/g, (loanInterestRate * 100).toFixed(2))
      .replace(/{{STATE}}/g, Assumptions.stateValues[illustrationRequest.state])
      .replace(/{{START_DATE}}/g, Utils.tzDate(new Date(startDate)).toMonthDY())
      .replace(
        /{{VARIABLE_RATE_MESSAGE}}/,
        illustrationRequest.loanRateType.toLowerCase() === 'variable'
          ? ' This rate may change on each policy anniversary date.'
          : ''
      );

    return this.getHtmlContent(style, body);
  }

  getHtmlContent_Numeric_Summary(illustrationRequest: IllustrationRequest, revised: boolean): string {
    const numericSummaryTable = this.populateNumericSummaryData(illustrationRequest);

    const style = `${Dividend_Static_Info.Style}
                       ${Style}`;

    let signature = Dividend_Static_Info.Signatures.replace(
      /{{APPLICANT_TEXT}}/,
      revised ? 'Owner' : 'Applicant (At time of application)<br>Owner (At time of delivery)'
    );

    // Get the appropriate set of text options for this state
    const textFill = StateSpecification.GetContentByState(
      illustrationRequest.state,
      StateSpecification.SignatureTextOptions,
      Dividend_Static_Info.SignatureTextOptions
    );

    // loop through properties to replace, and replace them, whether text or break numbers
    _.forIn(textFill, (value, key) => {
      const replacementText = '{{' + key + 'SignatureText' + '}}';

      if (typeof value === 'string') {
        signature = signature.replace(replacementText, value);
      } else if (typeof value === 'number') {
        // repeat breaks the specified number of times
        signature = signature.replace(replacementText, `<br>`.repeat(value));
      }
    });

    const body = `${Dividend_Static_Info.Dividends_Info}
                      ${numericSummaryTable}
                      ${signature}`;

    const NS_Html = this.getHtmlContent(style, body);

    return this.replaceHtmlContent(illustrationRequest, NS_Html);
  }

  getHtmlToPdfStreamUrlForAgentChannel(): string {
    return `${this.amorphic.config.nconf.get('pdfstreamUrl')}/fill/Agent`;
  }

  @serverOnly()
  getHeaders(): Header {
    const token = Utils.getAuthTokenForPdfstream();
    const header = {
      authorization: 'Bearer ' + token,
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
    return header;
  }

  getHtmlContent_NS_Complete(illustrationRequest: IllustrationRequest): string {
    return (
      this.getHtmlContent_NS_info(illustrationRequest) +
      this.getHtmlContent_NS_dividends(illustrationRequest) +
      this.getHTMLContentForPayments(illustrationRequest)
    );
  }

  getHTMLContentForPayments(illustrationRequest: IllustrationRequest): string {
    const styles = IACmain.styles + NS_PaymentOptions.style;

    let htmlContent =
      (StateSpecification.IAC.includes(illustrationRequest.state)
        ? this.getHTMLContent_NS_IAC(illustrationRequest)
        : '') +
      StaticContent.KEY_TERMS_USED_IN_ILLUSTRATION +
      this.getHtmlContent_NS_ColumnHeadingDefinitions(illustrationRequest) +
      this.getHtmlContent_NS_PaymentOptions(illustrationRequest);

    let cirTerms = ``;
    if (!!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR)) {
      cirTerms = `${StaticContent.INITIAL_BASE_BENEFIT_POOL_INFO}`;
    }
    htmlContent = htmlContent.replace(/{{InitialBaseBenefitPoolInfo}}/g, cirTerms);

    return this.getHtmlContent(styles, htmlContent);
  }

  @serverOnly()
  async getBody(
    illustrationRequest: IllustrationRequest,
    revised: boolean,
    isSupplemental: boolean = false
  ): Promise<Body[]> {
    const lastPageFooter = await this.getFilledFooter(illustrationRequest, true);
    const allButLastPageFooter = await this.getFilledFooter(illustrationRequest, false);
    const addAlirInfo =
      !!illustrationRequest.riders.find((rider) => rider.type === RiderType.ALIR) ||
      illustrationRequest.cashValueTransfer > 0;
    const isNYIllustration: boolean = illustrationRequest.state === 'NY';
    const mecStatus =
      illustrationRequest.mecStatus === 'Yes' || this.basicIllustrationData.illustration.payload.policy.mecYear > 0
        ? 'Yes'
        : 'No';
    const hasCirRider = !!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR);
    let productSuffix = ``;
    let cirClaimIllustrationResponse;
    let cirTabularPages: number = 0;
    if (hasCirRider) {
      productSuffix = ' with CIR';
      cirClaimIllustrationResponse = await this.illustrationService.getCirClaimIllustrationData(illustrationRequest);
      cirTabularPages = IllustrationSummary.getPagesFromRows(
        cirClaimIllustrationResponse.data.cirClaimIllustration.payload.policy.illustrationList.length,
        constants.illustrationRowsOnOtherPages,
        constants.illustrationRowsOnOtherPages
      );
    }

    const coverpage: Body = {
      html: await this.getHtmlContent_coverPage(illustrationRequest, revised),
    };

    let maindocument: Body[] = [
      {
        html: isSupplemental
          ? this.getHtmlContent_Supplemental_NS_Complete(illustrationRequest, true)
          : this.getHtmlContent_NS_Complete(illustrationRequest),
        footer: allButLastPageFooter,
      },
      {
        html: this.getHtmlContent_Numeric_Summary(illustrationRequest, revised),
        footer: allButLastPageFooter,
      },
    ];

    /*
    The following is just a temporary fix to solve the production bug involving incorrect page numbers in Illustrations.
    We are using the number of lines added to the illustration by every rider added to the policy
    and checking if it causes an overflow of text to a new page.
    */
    const policyRiders = illustrationRequest.riders as PolicyRider[];
    const isMec = mecStatus === 'Yes';
    const signaturePageNumber = HtmlPdfUtils.getSignaturePageNumber(
      policyRiders,
      illustrationRequest.dividendOption,
      illustrationRequest.waiverUnits,
      illustrationRequest.cashValueTransfer,
      isMec
    );

    const pagesBeforeTabular: number = isSupplemental ? constants.SupplementalPagesBeforeTabular : signaturePageNumber;

    let pageInfos: PageInfo[] = [
      {
        title: NS_info.Title,
        offset: 1,
        footer: allButLastPageFooter,
      },
      {
        title: Title,
        offset: pagesBeforeTabular - 1,
        footer: allButLastPageFooter,
      },
    ];

    const tabularPages: number = IllustrationSummary.getPagesFromRows(
      this.basicIllustrationData.illustration.payload.policy.illustrationList.length,
      constants.illustrationRowsOnFirstPage,
      constants.illustrationRowsOnOtherPages
    );

    const supplementalPages: number = isSupplemental
      ? IllustrationSummary.getPagesFromRows(
          this.supplementalIllustrationData.illustration.payload.policy.illustrationList.length,
          constants.illustrationRowsOnFirstPage,
          constants.illustrationRowsOnOtherPages
        )
      : 0;

    const totalPagesBeforeProductDisclosure: number =
      pagesBeforeTabular +
      tabularPages +
      supplementalPages +
      (addAlirInfo ? 1 : 0) +
      (hasCirRider ? cirTabularPages + 1 : 0);

    const totalPages: number =
      totalPagesBeforeProductDisclosure + (isNYIllustration ? constants.PagesInProductDisclosure : 0);

    // Add as many tabular values pages as needed, and add corresponding page info
    for (const i of _.range(tabularPages).map((i) => i + 1)) {
      maindocument = maindocument.concat({
        html: this.getHtmlContent_IllustrationTabularData(illustrationRequest, i),
        footer: allButLastPageFooter,
      } as Body);

      // this must be called before getFooter is called for lowerdividend options to show
      IllustrationBasedOnDiv.createLowerDividendFooterMap(illustrationRequest.dividendInterestAdjustment);

      const dividendOption: string = isSupplemental
        ? `Tabular${illustrationRequest.dividendOption}`
        : illustrationRequest.dividendOption;
      const incrementalPageInfo: PageInfo = {
        disclaimer: IllustrationBasedOnDiv.getFooter(dividendOption, illustrationRequest, isSupplemental),
        offset: pagesBeforeTabular - 1 + i,
        ...(i > 1 && { title: IllustrationHtml.title }),
      };

      const isLastPage = totalPages - incrementalPageInfo.offset <= 1;

      if (isLastPage) {
        incrementalPageInfo.footer = lastPageFooter;
      } else {
        incrementalPageInfo.footer = allButLastPageFooter;
      }

      pageInfos = pageInfos.concat(incrementalPageInfo);
    }

    if (isSupplemental) {
      // Add supplemental values
      for (const i of _.range(supplementalPages).map((i) => i + 1)) {
        maindocument = maindocument.concat({
          html: this.getHtmlContent_Supplemental_Values(illustrationRequest, i),
          footer: allButLastPageFooter,
        } as Body);

        // this must be called before getFooter is called for lowerdividend options to show
        IllustrationBasedOnDiv.createLowerDividendFooterMap(illustrationRequest.dividendInterestAdjustment);

        const incrementalPageInfo: PageInfo = {
          disclaimer: IllustrationBasedOnDiv.getFooter(
            `Supplemental${illustrationRequest.dividendOption}`,
            illustrationRequest,
            true
          ),
          offset: constants.SupplementalPagesBeforeTabular - 1 + i + tabularPages,
          ...(i > 1 && { title: IllustrationHtml.supplementalTitle }),
        };

        const isLastPage = totalPages - incrementalPageInfo.offset <= 1;

        if (isLastPage) {
          incrementalPageInfo.footer = lastPageFooter;
        } else {
          incrementalPageInfo.footer = allButLastPageFooter;
        }

        pageInfos = pageInfos.concat(incrementalPageInfo);
      }
    }

    if (hasCirRider) {
      const body = StaticContent.CIR_INFO_FULL_PAGE;
      const currentPage = addAlirInfo
        ? totalPagesBeforeProductDisclosure - cirTabularPages - 2
        : totalPagesBeforeProductDisclosure - cirTabularPages - 1;
      maindocument = maindocument.concat({
        html: this.getHtmlContent(BasicIllustrationStyles.general, body),
        footer: allButLastPageFooter,
      });
      pageInfos = pageInfos.concat({
        title: StaticContent.CIR_INFO_TITLE,
        offset: currentPage,
        footer: allButLastPageFooter,
      });

      for (const i of _.range(cirTabularPages).map((i) => i + 1)) {
        maindocument = maindocument.concat({
          html: this.getHtmlContent_CirClaimIllustrationTabularData(cirClaimIllustrationResponse, i),
          footer: allButLastPageFooter,
        } as Body);

        const incrementalPageInfo: PageInfo = {
          offset: currentPage + i,
          disclaimer: CirClaimIllustration.getFooter(),
          title: IllustrationHtml.cirClaimIllustrationTitle,
        };

        const isLastPage = totalPages - incrementalPageInfo.offset <= 1;

        if (isLastPage) {
          incrementalPageInfo.footer = lastPageFooter;
        } else {
          incrementalPageInfo.footer = allButLastPageFooter;
        }

        pageInfos = pageInfos.concat(incrementalPageInfo);
      }
    }

    if (addAlirInfo) {
      const body = StaticContent.ALIR_INFO_FULL_PAGE.replace(/{{ALIR_PERCENTAGE}}/g, '7.5%');
      maindocument = maindocument.concat({
        html: this.getHtmlContent(BasicIllustrationStyles.general, body),
        footer: lastPageFooter,
      });
      pageInfos = pageInfos.concat({
        title: StaticContent.ALIR_INFO_TITLE,
        offset: totalPagesBeforeProductDisclosure - 1,
        footer: lastPageFooter,
      });
    }

    this.addHeadersAndFooters(
      maindocument,
      pageInfos,
      `MassMutual Whole Life ${constants.ProductSelection[illustrationRequest.productCode] + productSuffix}`,
      'Life Insurance Illustration',
      isSupplemental
    );

    const arrayOfPages = [...[coverpage], ...maindocument];
    if (isNYIllustration) {
      /**
       * add Product Summary
       * this goes before "coverpage"
       */
      const productSummary: Body[] = [
        { html: this.getHtmlContent(NYStaticContent.PRODUCT_SUMMARY.styles, NYStaticContent.PRODUCT_SUMMARY.body) },
      ];
      const productSummaryPageInfos: PageInfo[] = [{ offset: 0, footer: lastPageFooter }];
      this.addHeadersAndFooters(
        productSummary,
        productSummaryPageInfos,
        'MassMutual Whole Life',
        'Product Summary',
        isSupplemental
      );
      productSummary.push({
        html: this.getHtmlContent(
          NYStaticContent.INTENTIONAL_BLANK_PAGE.styles,
          NYStaticContent.INTENTIONAL_BLANK_PAGE.body
        ),
      });
      arrayOfPages.unshift(...productSummary);

      /**
       * add Product Disclosure
       * this goes after the tabular values and supplemental values pages
       */
      const productDisclosureHtml: string = NYStaticContent.PRODUCT_DISCLOSURE.body
        .replace(/{{PRODUCT}}/g, constants.ProductSelection[illustrationRequest.productCode])
        .replace(/{{PRODUCT_PAYABLE_COPY}}/g, constants.PayableCopy[illustrationRequest.productCode].toLowerCase());
      const productDisclosure: Body = {
        html: this.getHtmlContent(NYStaticContent.PRODUCT_DISCLOSURE.styles, productDisclosureHtml),
      };
      const productDisclosurePageInfos: PageInfo = {
        offset: totalPagesBeforeProductDisclosure,
        footer: allButLastPageFooter,
      };
      this.addHeadersAndFooters(
        [productDisclosure],
        [productDisclosurePageInfos],
        `MassMutual Whole Life ${constants.ProductSelection[illustrationRequest.productCode]}`,
        'Product Disclosure - For use in New York',
        isSupplemental
      );
      arrayOfPages.push(productDisclosure);
    }

    if (isSupplemental) {
      // Add client input summary
      const clientInputSummary: Body = {
        html: this.getClientInputSummaryHtmlContent(
          BasicIllustrationStyles.general,
          this.getClientInputSummary(illustrationRequest)
        ),
        footer: 'Page',
      };
      const clientInputSummaryPageInfos: PageInfo = {
        title: 'Client Input Summary',
        offset: totalPages - 1,
        footer: '',
      };
      this.addHeadersAndFooters(
        [clientInputSummary],
        [clientInputSummaryPageInfos],
        `MassMutual Whole Life ${constants.ProductSelection[illustrationRequest.productCode]}`,
        'Life Insurance Illustration',
        isSupplemental
      );
      arrayOfPages.push(clientInputSummary);
    }

    return arrayOfPages.map((section, i) => {
      // appends common styles exactly once to each section
      section.html = section.html.replace(
        `</style>`,
        `${BasicIllustrationStyles.general}${BasicIllustrationStyles.fonts}${Footer.getFooterStyle()}</style>`
      );
      section.html = section.html.replace(/{{pages}}/g, `${isNYIllustration && i === 0 ? 1 : totalPages}`);

      // add PDFStream info
      section.formOrder = `${i}`;
      section.formType = 'htmlFile';
      return section;
    });
  }

  addHeadersAndFooters(
    documents: Body[],
    pageInfos: PageInfo[],
    headerLine1: string,
    headerLine2: string,
    isSupplemental: boolean
  ) {
    _.zipWith(documents, pageInfos, (section, pageInfo) => {
      // append header and footer styles and bodies into the html documents of the sections (but not the coverpage)
      section.html = section.html.replace(`</style>`, `${TemplateHeader.getHeaderStyle()}</style>`);
      section.html = section.html.replace(
        `</body>`,
        `${TemplateHeader.getHeaderBody(headerLine1, headerLine2)}${pageInfo.footer}</body>`
      );

      const pageOptions = {
        header: {
          height: constants.HeaderHeight.Normal,
        },
        footer: {
          height: constants.FooterHeight.Normal,
          paginationOffset: pageInfo.offset,
        },
      };

      // add title and/or disclaimer as specifid in pageInfos, and adjust heights accordingly
      if (pageInfo.title) {
        section.html = section.html.replace(
          `<hr class="headerbreak"/>`,
          `<hr class="headerbreak"/><h1 class="p-center">${pageInfo.title}</h1>`
        );
        pageOptions.header.height = constants.HeaderHeight.Title;
      }
      if (pageInfo.disclaimer) {
        section.html = section.html.replace(
          `<hr class="footerbreak"/>`,
          `${pageInfo.disclaimer}<hr class="footerbreak"/>`
        );
        pageOptions.footer.height = isSupplemental
          ? constants.FooterHeight.SupplementalDisclaimer
          : constants.FooterHeight.Disclaimer;
      }

      (section as Body).options = { ...(section as Body).options, ...pageOptions };

      return section;
    });
  }

  @remote({ on: 'server' })
  async fetchDataFromProductService(illustrationRequest: IllustrationRequest, isSupplemental?: boolean): Promise<void> {
    const illustrationData = [],
      illustrationDataIndices = [];

    // Clear out illustration data each time we make a request
    this.basicIllustrationData = null;
    this.basicIllustrationDataMidpoint = null;
    this.supplementalIllustrationData = null;

    illustrationDataIndices.push('basicIllustrationData');
    illustrationData.push(this.illustrationService.getIllustrationData(illustrationRequest));

    const midpointRequest: IllustrationRequest = Object.assign({}, illustrationRequest, { dividendRatePercent: 0.5 });
    illustrationDataIndices.push('basicIllustrationDataMidpoint');
    illustrationData.push(this.illustrationService.getIllustrationData(midpointRequest));
    illustrationRequest.asOfDate = illustrationRequest.startDate;
    illustrationDataIndices.push('paymentOptions');
    illustrationData.push(this.illustrationService.getPaymentPayloadData(illustrationRequest));
    if (isSupplemental) {
      illustrationDataIndices.push('supplementalIllustrationData');
      illustrationData.push(this.illustrationService.getSupplementalIllustrationData(illustrationRequest));
    }
    try {
      const responses = await Promise.all(illustrationData);
      for (let i = 0; i < responses.length; i++) {
        if (responses[i].Exception) {
          throw responses[i].Exception;
        }
        switch (illustrationDataIndices[i]) {
          case 'basicIllustrationData':
            this.basicIllustrationData = responses[i].data;
            break;
          case 'basicIllustrationDataMidpoint':
            this.basicIllustrationDataMidpoint = responses[i].data;
            break;
          case 'paymentOptions':
            this.basicIllustrationAdditionalData.paymentOptions = this.getPaymentOptions(
              responses[i].data.premiums.payload.policies,
              illustrationRequest
            );
            break;
          case 'supplementalIllustrationData':
            this.supplementalIllustrationData = responses[i].data;
            break;
        }
      }
      responses.map((response, i) => {
        if (response.Exception) {
          throw response.Exception;
        }
      });
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'HtmlPdfController',
          function: 'fetchDataFromProductService',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          error: error,
          category: Category.request,
        },
        `Exception occurred while fetching illustration data from product.`
      );
    }
  }

  @remote({ on: 'server' })
  async getAlirAmountAtIsssueFromIllustration(illustrationRequest: IllustrationRequest): Promise<number> {
    try {
      const response = await this.illustrationService.getIllustrationData(illustrationRequest);
      return response.data.illustration.payload.policy.alirAtIssueUnits;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'HtmlPdfController',
          function: 'getAlirAmountAtIsssueFromIllustration',
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          category: Category.request,
          error: error,
        },
        'Failed to retrieve ALIR at issue units from Product Illustration'
      );
      return null;
    }
  }

  @remote({ on: 'server' })
  async generateBasicIllustrationPDF(illustrationRequest: IllustrationRequest, revised: boolean) {
    try {
      await this.fetchDataFromProductService(illustrationRequest, null);
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error({
        module: 'HtmlPdfController',
        function: 'generateBasicIllustration',
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        error: error,
        category: Category.request,
      });
      throw err;
    }
    const headers = this.getHeaders();

    const body = await this.getBody(illustrationRequest, revised);

    const requestBody = {
      data: body,
      page: {
        height: '11in',
        width: '8.5in',
        border: '0.25in',
        header: {
          height: constants.HeaderHeight.Normal,
        },
        footer: {
          height: constants.FooterHeight.Normal,
        },
      },
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfController',
        function: 'generateBasicIllustration',
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        category: Category.request,
      },
      'Html Content generation complete - sending to pdf stream now'
    );

    const options = {
      url: this.getHtmlToPdfStreamUrlForAgentChannel(),
      json: true,
      body: requestBody,
      method: 'POST',
      headers: headers,
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfController',
        function: 'generateBasicIllustration',

        data: {
          url: options.url,
          headers: headers,
        },
        category: Category.request,
      },
      'Converting html to pdf using pdfstream service'
    );

    return requestPromise(options);
  }

  @remote({ on: 'server' })
  async generateTransientRevisedBasicIllustrationPDF(
    illustrationRequest: IllustrationRequest,
    amendmentValues: AmendmentForm
  ) {
    const rbi = await this.generateBasicIllustrationPDF(illustrationRequest, true);
    this.transientRBIInBase64 = rbi.toString('base64');
    this.transientAmendmentValues = amendmentValues;
  }

  private populateNumericSummaryData(illustrationRequest: IllustrationRequest): string {
    const illustrationList = this.basicIllustrationData.illustration.payload.policy.illustrationList;
    const illustrationListMidpoint = this.basicIllustrationDataMidpoint.illustration.payload.policy.illustrationList;

    const age = CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate);
    const maxLen: number = 70 - age;

    if (illustrationList.length < maxLen || illustrationListMidpoint.length < maxLen) {
      const error = {
        isHumanRelated: false,
        data: {
          illustrationList: JSON.stringify(illustrationList),
          illustrationListMidpoint: JSON.stringify(illustrationListMidpoint),
        },
      };
      LoggerSingleton.getInstance().error({
        module: 'HtmlPdfController',
        function: 'populateNumericSummaryData',

        error: error,
        category: Category.request,
      });

      return;
    }

    const year5 = illustrationList[4];
    const year10 = illustrationList[9];
    const year20 = illustrationList[19];
    const age70 = illustrationList[maxLen - 1];

    const year5Midpoint = illustrationListMidpoint[4];
    const year10Midpoint = illustrationListMidpoint[9];
    const year20Midpoint = illustrationListMidpoint[19];
    const age70Midpoint = illustrationListMidpoint[maxLen - 1];
    const tableHtml = Boolean(this.findRiderByType(illustrationRequest.riders, RiderType.RTR))
      ? NumericRTRSummaryTableHtml
      : NumericSummaryTableHtml;
    const html = tableHtml.Table.replace(
      /{{AnnualPremium5}}/,
      Utils.formatNumber(Utils.round(year5.guaranteedContractPremiumBegYear, 0))
    )
      .replace(/{{AnnualPremium10}}/, Utils.formatNumber(Utils.round(year10.guaranteedContractPremiumBegYear, 0)))
      .replace(/{{AnnualPremium20}}/, Utils.formatNumber(Utils.round(year20.guaranteedContractPremiumBegYear, 0)))
      .replace(/{{AnnualPremium70}}/, Utils.formatNumber(Utils.round(age70.guaranteedContractPremiumBegYear, 0)))
      .replace(/{{GuaranteedTotalCash5}}/, Utils.formatNumber(Utils.round(year5.guaranteedCashValueEndYear, 0)))
      .replace(/{{GuaranteedTotalCash10}}/, Utils.formatNumber(Utils.round(year10.guaranteedCashValueEndYear, 0)))
      .replace(/{{GuaranteedTotalCash20}}/, Utils.formatNumber(Utils.round(year20.guaranteedCashValueEndYear, 0)))
      .replace(/{{GuaranteedTotalCash70}}/, Utils.formatNumber(Utils.round(age70.guaranteedCashValueEndYear, 0)))
      .replace(/{{GuaranteedDeath5}}/, Utils.formatNumber(Utils.round(year5.guaranteedDeathBenefitEndYear, 0)))
      .replace(/{{GuaranteedDeath10}}/, Utils.formatNumber(Utils.round(year10.guaranteedDeathBenefitEndYear, 0)))
      .replace(/{{GuaranteedDeath20}}/, Utils.formatNumber(Utils.round(year20.guaranteedDeathBenefitEndYear, 0)))
      .replace(/{{GuaranteedDeath70}}/, Utils.formatNumber(Utils.round(age70.guaranteedDeathBenefitEndYear, 0)))
      .replace(
        /{{MidpointContractPremium5}}/,
        Utils.formatNumber(Utils.round((year5.guaranteedContractPremiumBegYear + year5.contractPremiumBegYear) / 2, 0))
      )
      .replace(
        /{{MidpointContractPremium10}}/,
        Utils.formatNumber(
          Utils.round((year10.guaranteedContractPremiumBegYear + year10.contractPremiumBegYear) / 2, 0)
        )
      )
      .replace(
        /{{MidpointContractPremium20}}/,
        Utils.formatNumber(
          Utils.round((year20.guaranteedContractPremiumBegYear + year20.contractPremiumBegYear) / 2, 0)
        )
      )
      .replace(
        /{{MidpointContractPremium70}}/,
        Utils.formatNumber(Utils.round((age70.guaranteedContractPremiumBegYear + age70.contractPremiumBegYear) / 2, 0))
      )
      .replace(/{{MidpointTotalCash5}}/, Utils.formatNumber(Utils.round(year5Midpoint.totalCashValueEndYear, 0)))
      .replace(/{{MidpointTotalCash10}}/, Utils.formatNumber(Utils.round(year10Midpoint.totalCashValueEndYear, 0)))
      .replace(/{{MidpointTotalCash20}}/, Utils.formatNumber(Utils.round(year20Midpoint.totalCashValueEndYear, 0)))
      .replace(/{{MidpointTotalCash70}}/, Utils.formatNumber(Utils.round(age70Midpoint.totalCashValueEndYear, 0)))
      .replace(/{{MidpointDeath5}}/, Utils.formatNumber(Utils.round(year5Midpoint.totalDeathBenefitEndYear, 0)))
      .replace(/{{MidpointDeath10}}/, Utils.formatNumber(Utils.round(year10Midpoint.totalDeathBenefitEndYear, 0)))
      .replace(/{{MidpointDeath20}}/, Utils.formatNumber(Utils.round(year20Midpoint.totalDeathBenefitEndYear, 0)))
      .replace(/{{MidpointDeath70}}/, Utils.formatNumber(Utils.round(age70Midpoint.totalDeathBenefitEndYear, 0)))
      .replace(/{{CurrentContractPremium5}}/, Utils.formatNumber(Utils.round(year5.contractPremiumBegYear, 0)))
      .replace(/{{CurrentContractPremium10}}/, Utils.formatNumber(Utils.round(year10.contractPremiumBegYear, 0)))
      .replace(/{{CurrentContractPremium20}}/, Utils.formatNumber(Utils.round(year20.contractPremiumBegYear, 0)))
      .replace(/{{CurrentContractPremium70}}/, Utils.formatNumber(Utils.round(age70.contractPremiumBegYear, 0)))
      .replace(/{{CurrentTotalCash5}}/, Utils.formatNumber(Utils.round(year5.totalCashValueEndYear, 0)))
      .replace(/{{CurrentTotalCash10}}/, Utils.formatNumber(Utils.round(year10.totalCashValueEndYear, 0)))
      .replace(/{{CurrentTotalCash20}}/, Utils.formatNumber(Utils.round(year20.totalCashValueEndYear, 0)))
      .replace(/{{CurrentTotalCash70}}/, Utils.formatNumber(Utils.round(age70.totalCashValueEndYear, 0)))
      .replace(/{{CurrentDeath5}}/, Utils.formatNumber(Utils.round(year5.totalDeathBenefitEndYear, 0)))
      .replace(/{{CurrentDeath10}}/, Utils.formatNumber(Utils.round(year10.totalDeathBenefitEndYear, 0)))
      .replace(/{{CurrentDeath20}}/, Utils.formatNumber(Utils.round(year20.totalDeathBenefitEndYear, 0)))
      .replace(/{{CurrentDeath70}}/, Utils.formatNumber(Utils.round(age70.totalDeathBenefitEndYear, 0)));

    return html;
  }

  @remote({ on: 'server' })
  async generateBasicIllustrationHTML(illustrationRequest: IllustrationRequest, revised: boolean) {
    await this.fetchDataFromProductService(illustrationRequest);
    const body = await this.getBody(illustrationRequest, revised);
    return body;
  }

  public createDefaultSafeIllustrationRequest(
    isSupplemental: boolean = false,
    safeInviteInfo: SafeInviteInfo
  ): IllustrationRequest {
    const cpQuote = safeInviteInfo.safeWholeQuote;
    const cpTermQuote = safeInviteInfo.safeTermQuote;
    const productSwitchedFromTermToWhole = safeInviteInfo.safePolicyInfo.hasWhole && safeInviteInfo.hasTermQuote;
    let effectiveDate: Date = new Date();
    let faceUnits: number;
    let dob;
    let waiverUnits = 0;
    const hasApplicationAndPolicy: boolean =
      !!safeInviteInfo.safePolicyInfo.primaryPolicy || productSwitchedFromTermToWhole;
    let rateClass;
    let primaryPolicy: SafeBasePolicy;
    let dividendOption;
    if (cpQuote) {
      faceUnits = cpQuote.faceAmount;
      dob = cpQuote.dateOfBirth;
      effectiveDate = cpQuote.effectiveDate || new Date();
      waiverUnits = cpQuote.hasWOP ? faceUnits : 0;
      rateClass = RateClasses[cpQuote.healthClass];
      dividendOption = cpQuote.dividend;
    }

    if (productSwitchedFromTermToWhole) {
      primaryPolicy = safeInviteInfo.safePolicyInfo.primaryPolicy;
      faceUnits = primaryPolicy.faceAmount;
      dob = cpTermQuote.dateOfBirth;
      effectiveDate = primaryPolicy.effectiveDate || new Date();
      waiverUnits = primaryPolicy.hasWOP ? faceUnits : 0;
      rateClass = RateClasses[cpTermQuote.healthClass];
      dividendOption = primaryPolicy.dividendOption;
    }

    if (safeInviteInfo.isAgentSubmittedApp) {
      primaryPolicy = safeInviteInfo.safePolicyInfo.primaryPolicy;
      faceUnits = primaryPolicy.faceAmount;
      dob = primaryPolicy.insured.dob;
      effectiveDate = primaryPolicy.effectiveDate || new Date();
      waiverUnits = primaryPolicy.hasWOP ? primaryPolicy.faceAmount : 0;
      rateClass = RateClasses.find(primaryPolicy.healthClass);
      dividendOption = primaryPolicy.dividendOption;
    } else if (hasApplicationAndPolicy) {
      primaryPolicy = safeInviteInfo.safePolicyInfo.primaryPolicy;
      // If app and policy exist, determine if we can use the quote rate class
      // or if we need to use standard/standard tobacco instead based on face amount
      const wholeLifePolicy = primaryPolicy as SafeWholePolicy;
      const faceAndRtrAmount = wholeLifePolicy.hasRTR
        ? wholeLifePolicy.faceAmount + wholeLifePolicy.rtr.amount
        : wholeLifePolicy.faceAmount;
      rateClass = CommonUtils.determineRatingClass(
        productSwitchedFromTermToWhole ? cpTermQuote.healthClass : cpQuote.healthClass,
        faceAndRtrAmount,
        primaryPolicy.product,
        primaryPolicy.productType
      );
      dividendOption = primaryPolicy.dividendOption;
    }
    let mecStatus: 'Yes' | 'No';
    let cashValueTransfer: number;
    let costBasis: number;
    let expectedArrival: number;
    let applyTowards: string;

    if (hasApplicationAndPolicy) {
      const wholeLifePolicy = primaryPolicy as SafeWholePolicy;
      const alirRider: SafePolicyRider = wholeLifePolicy.alir;
      const exchange1035ForAlir: SafeExchange1035 = alirRider && alirRider.exchange1035;
      if (exchange1035ForAlir) {
        mecStatus = exchange1035ForAlir.isMEC ? 'Yes' : 'No';
        cashValueTransfer = exchange1035ForAlir.cashValueTransfer;
        costBasis = exchange1035ForAlir.costBasis;
        expectedArrival = exchange1035ForAlir.expectedArrival;
        applyTowards = exchange1035ForAlir.applyTowards;
      }
    } else {
      mecStatus = cpQuote.quoteExchange1035 && cpQuote.quoteExchange1035.isMec ? 'Yes' : 'No';
      cashValueTransfer = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.cashValueTransfer : 0; //TODO check if 0 being the default is ok
      costBasis = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.costBasis : 0; //TODO check if 0 being the default is ok
      expectedArrival = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.expectedArrival : 0;
      applyTowards = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.applyTowards : null;
    }
    return {
      requestId: Utils.generateUuid(),
      dividendOption: dividendOption,
      startDate: effectiveDate,
      gender: hasApplicationAndPolicy ? primaryPolicy.insured.gender : cpQuote.gender,
      dob: hasApplicationAndPolicy ? primaryPolicy.insured.dob : dob,
      duration: hasApplicationAndPolicy ? primaryPolicy.term : cpQuote.term,
      productCode: hasApplicationAndPolicy ? primaryPolicy.product : cpQuote.product,
      state: hasApplicationAndPolicy ? primaryPolicy.contractState : cpQuote.contractState,
      rateClass: rateClass.stringCode,
      paymentFrequency: hasApplicationAndPolicy
        ? Utils.toCapitalizedWords(primaryPolicy.paymentFrequency)
        : cpQuote.paymentFrequency,
      faceAmount: hasApplicationAndPolicy ? primaryPolicy.faceAmount : faceUnits,
      waiverUnits: hasApplicationAndPolicy ? (primaryPolicy.hasWOP ? primaryPolicy.faceAmount : 0) : waiverUnits,
      flatExtra: hasApplicationAndPolicy ? primaryPolicy.flatExtra : cpQuote.flatExtra,
      taxRate: hasApplicationAndPolicy
        ? CoverpathWholeLifeQuote.defaultTaxBracket
        : Utils.round(cpQuote.taxBracket / 100, 2),
      loanRateType: hasApplicationAndPolicy ? primaryPolicy.loanType : cpQuote.loanType,
      dividendRatePercent: 1,
      dividendInterestAdjustment: isSupplemental ? cpQuote.dividendInterestAdjustment : 0,
      customerPerson: IllustrationRequestHelpers.createIllustrationForSafeCustomer(safeInviteInfo),
      agent: IllustrationRequestHelpers.createIllustrationForSafeAgent(safeInviteInfo.safePolicyInfo.primaryAgent),
      mecStatus: mecStatus,
      riders: hasApplicationAndPolicy
        ? IllustrationRequestHelpers.createIllustrationForSafeRiders(primaryPolicy as SafeWholePolicy)
        : IllustrationRequestHelpers.createIllustrationRidersForSafeQuote(cpQuote),
      cashValueTransfer,
      costBasis,
      expectedArrival,
      applyTowards,
      bankSale: cpQuote ? cpQuote.bankSale : false,
    };
  }

  public createDefaultIllustrationRequest(
    customer: CoverpathCustomer,
    agent: Agent,
    isSupplemental: boolean = false
  ): IllustrationRequest {
    const effectiveDate: Date = customer.wholeLifeQuote.effectiveDate || new Date();
    const faceUnits: number = customer.wholeLifeQuote.faceAmount;
    const cpQuote: CoverpathWholeLifeQuote = customer.wholeLifeQuote;
    const dob = customer.wholeLifeQuote.dateOfBirth;
    const waiverUnits: number = cpQuote.waiverOfPremium ? faceUnits : 0;
    const hasApplicationAndPolicy: boolean = !!(customer.application && customer.application.primaryPolicy);
    let rateClass = RateClasses[cpQuote.healthClass];
    let primaryPolicy: CoverpathPolicy;
    let dividendOption = cpQuote.dividend;
    // If app and policy exist, determine if we can use the quote rate class
    // or if we need to use standard/standard tobacco instead based on face amount
    if (hasApplicationAndPolicy) {
      primaryPolicy = customer.application.primaryPolicy as CoverpathPolicy;
      const faceAndRtrAmount = primaryPolicy.hasRtrRider
        ? primaryPolicy.faceAmount + primaryPolicy.rtrRiderAmount
        : primaryPolicy.faceAmount;
      rateClass = CommonUtils.determineRatingClass(
        cpQuote.healthClass,
        faceAndRtrAmount,
        primaryPolicy.product,
        primaryPolicy.productType
      );
      dividendOption = primaryPolicy.dividendOption;
    }
    let mecStatus: 'Yes' | 'No';
    let cashValueTransfer: number;
    let costBasis: number;
    let expectedArrival: number;
    let applyTowards: string;

    if (hasApplicationAndPolicy) {
      const alirRider: PolicyRider = primaryPolicy.alirRider;
      const exchange1035ForAlir: Exchange1035 = alirRider && alirRider.exchange1035;
      if (exchange1035ForAlir) {
        mecStatus = exchange1035ForAlir.isMec ? 'Yes' : 'No';
        cashValueTransfer = exchange1035ForAlir.cashValueTransfer;
        costBasis = exchange1035ForAlir.costBasis;
        expectedArrival = exchange1035ForAlir.expectedArrival;
        applyTowards = exchange1035ForAlir.applyTowards;
      }
    } else {
      mecStatus = cpQuote.quoteExchange1035 && cpQuote.quoteExchange1035.isMec ? 'Yes' : 'No';
      cashValueTransfer = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.cashValueTransfer : 0; //TODO check if 0 being the default is ok
      costBasis = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.costBasis : 0; //TODO check if 0 being the default is ok
      expectedArrival = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.expectedArrival : 0;
      applyTowards = cpQuote.quoteExchange1035 ? cpQuote.quoteExchange1035.applyTowards : null;
    }
    return {
      requestId: Utils.generateUuid(),
      dividendOption: dividendOption,
      startDate: effectiveDate,
      gender: hasApplicationAndPolicy ? primaryPolicy.insured.person.gender : cpQuote.gender,
      dob: hasApplicationAndPolicy ? primaryPolicy.insured.person.dob : dob,
      duration: hasApplicationAndPolicy ? primaryPolicy.term : cpQuote.term,
      productCode: hasApplicationAndPolicy ? primaryPolicy.product : cpQuote.product,
      state: cpQuote.contractState,
      rateClass: rateClass.stringCode,
      paymentFrequency: hasApplicationAndPolicy
        ? Utils.toCapitalizedWords(primaryPolicy.paymentFrequency)
        : cpQuote.paymentFrequency,
      faceAmount: hasApplicationAndPolicy ? primaryPolicy.faceAmountWithPendingAmendment() : faceUnits,
      waiverUnits: hasApplicationAndPolicy
        ? primaryPolicy.waiver
          ? primaryPolicy.faceAmountWithPendingAmendment()
          : 0
        : waiverUnits,
      flatExtra: cpQuote.flatExtra,
      taxRate: Utils.round(cpQuote.taxBracket / 100, 2),
      loanRateType: cpQuote.loanType,
      dividendRatePercent: 1,
      dividendInterestAdjustment: isSupplemental ? cpQuote.dividendInterestAdjustment : 0,
      customerPerson: IllustrationRequestHelpers.createIllustrationPerson(customer.primaryCustomer.person),
      agent: IllustrationRequestHelpers.createIllustrationAgent(agent),
      mecStatus: mecStatus,
      riders: hasApplicationAndPolicy
        ? IllustrationRequestHelpers.createIllustrationRiders(primaryPolicy.riders)
        : IllustrationRequestHelpers.createIllustrationRidersForQuote(cpQuote.riders),
      cashValueTransfer,
      costBasis,
      expectedArrival,
      applyTowards,
      bankSale: cpQuote.bankSale,
    };
  }

  public createMostRecentAppIllustrationRequest(customer: CoverpathCustomer, agent: Agent): IllustrationRequest {
    const currentDate: Date = new Date();
    const latestApplication = customer.getLatestApplication();
    const policy: CoverpathPolicy = latestApplication.primaryPolicy as CoverpathPolicy;
    const faceUnits: number = policy.faceAmount;
    const dob = policy.insured.person.dob;
    const waiverUnits: number = policy.waiver ? faceUnits : 0;

    return {
      requestId: Utils.generateUuid(),
      dividendOption: policy.dividendOption,
      startDate: currentDate,
      gender: policy.insured.person.gender,
      dob: dob,
      duration: policy.term,
      productCode: policy.product,
      state: policy.getApplicationState(),
      rateClass: RateClasses.find(policy.getCurrentRateClass()).stringCode,
      paymentFrequency: Utils.capitalizeFirstLetter(policy.paymentFrequency),
      faceAmount: faceUnits,
      waiverUnits: waiverUnits,
      flatExtra: policy.flatExtras,
      taxRate: policy.customer.wholeLifeQuote && Utils.round(policy.customer.wholeLifeQuote.taxBracket / 100, 2),
      loanRateType: policy.loanInterestType,
      dividendRatePercent: 1,
      customerPerson: IllustrationRequestHelpers.createIllustrationPerson(
        latestApplication.primaryPolicy.insured.person
      ),
      agent: IllustrationRequestHelpers.createIllustrationAgent(agent),
      mecStatus:
        policy.alirRider && policy.alirRider.exchange1035 && policy.alirRider.exchange1035.isMec ? 'Yes' : 'No',
      riders: IllustrationRequestHelpers.createIllustrationRiders(policy.riders),
      cashValueTransfer:
        policy.alirRider && policy.alirRider.exchange1035 ? policy.alirRider.exchange1035.cashValueTransfer : 0, //TODO check if 0 being the default is ok
      costBasis: policy.alirRider && policy.alirRider.exchange1035 ? policy.alirRider.exchange1035.costBasis : 0, //TODO check if 0 being the default is ok
      bankSale: policy.customer.wholeLifeQuote && policy.customer.wholeLifeQuote.bankSale,
      expectedArrival:
        policy.alirRider && policy.alirRider.exchange1035 ? policy.alirRider.exchange1035.expectedArrival : 0,
      applyTowards: policy.alirRider && policy.alirRider.exchange1035 ? policy.alirRider.exchange1035.applyTowards : '',
    };
  }
  /**
   * Returns true if insured is juvenile
   * @param illustrationRequest
   */
  private isInsuredJuvenile(illustrationRequest: IllustrationRequest): boolean {
    const insuredDob = illustrationRequest.dob;
    const asOfDate = illustrationRequest.asOfDate || Utils.now();
    const insuranceAgeDate = Utils.getCurrentMidYearDate(insuredDob, asOfDate);
    const insuredAge = Utils.yearsBetween(insuredDob, asOfDate) + (insuranceAgeDate <= asOfDate ? 1 : 0);
    // For illustration, we use 14 (NOT 18) as the up limit for juvenile. If insured is < 14, show rate class as "Juvenile" instead
    // of the real rate class.
    return insuredAge < 14;
  }

  async getMecDataFromProduct(illustrationRequest: IllustrationRequest): Promise<boolean> {
    if (illustrationRequest.mecStatus === 'Yes') {
      return true;
    }
    const response = await this.illustrationService.getMecDataFromProduct(illustrationRequest);
    return response.data.illustration.payload.policy.mecYear > 0;
  }

  // Concept Illustration functions
  @remote({ on: 'server' })
  async generateConceptIllustrationPDF(illustrationRequest: IllustrationRequest, revised: boolean) {
    await this.fetchDataFromProductService(illustrationRequest, true);
    const headers = this.getHeaders();

    const body = await this.getBody(illustrationRequest, revised, true);

    const requestBody = {
      data: body,
      page: {
        height: '11in',
        width: '8.5in',
        border: '0.25in',
        header: {
          height: constants.HeaderHeight.Normal,
        },
        footer: {
          height: constants.FooterHeight.Normal,
        },
      },
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfController',
        function: 'generateConceptIllustration',
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          illustrationRequest: IllustrationService.cleanIllustrationRequestForLogging(illustrationRequest),
        },
        category: Category.request,
      },
      'Html Content generation complete - sending to pdf stream now'
    );

    const options = {
      url: this.getHtmlToPdfStreamUrlForAgentChannel(),
      json: true,
      body: requestBody,
      method: 'POST',
      headers: headers,
    };

    LoggerSingleton.getInstance().info(
      {
        module: 'HtmlPdfController',
        function: 'generateConceptIllustration',
        data: {
          url: options.url,
          headers: headers,
        },

        category: Category.request,
      },
      'Converting html to pdf using pdfstream service'
    );

    return requestPromise(options);
  }

  @serverOnly()
  getHtmlContent_Supplemental_NS_Complete(illustrationRequest: IllustrationRequest, isSupplemental?: boolean): string {
    return (
      this.getHtmlContent_NS_info(illustrationRequest, isSupplemental) +
      this.getHtmlContent_Supplemental_NS_dividends(illustrationRequest) +
      this.getHTMLContentForPaymentsSupplemental(illustrationRequest)
    );
  }

  getHtmlContent_Supplemental_Values(illustrationRequest: IllustrationRequest, pageNumber: number): string {
    //TODO : HL-6890 : Once we have real data the number of rows on a page and total number of pages will be calculated here
    const rowsOnPage1 = constants.illustrationRowsOnFirstPage;
    const rowsOnOtherPages = constants.illustrationRowsOnOtherPages;

    const paymentOptions = this.basicIllustrationAdditionalData.paymentOptions;
    let body: string = '';

    if (pageNumber === 1) {
      const startIndex = 0;
      body += `${IllustrationSummary.getSummary(illustrationRequest)}`;
      body += `${IllustrationBasedOnDiv.getTableTitle(true)}`;
      body += `${IllustrationBasedOnDiv.getSupplementalIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnPage1,
        this.supplementalIllustrationData
      )}`;
    } else {
      const startIndex = pageNumber > 1 ? rowsOnPage1 + rowsOnOtherPages * (pageNumber - 2) : 0;
      body += `${IllustrationBasedOnDiv.getSupplementalIllustrationTableBasedOnDiv(
        illustrationRequest,
        startIndex,
        rowsOnOtherPages,
        this.supplementalIllustrationData
      )}`;
    }
    const style: string = `${IllustrationSummary.getStyles()}`;
    let hasAlir = false;

    body = body
      .replace(/{{FaceAmount}}/g, Utils.formatNumber(illustrationRequest.faceAmount))
      .replace(/{{BasePremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.basePremium, 2), 2))
      .replace(/{{BasePremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.basePremium, 2), 2))
      .replace(/{{WaiverAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.waiver, 2), 2))
      .replace(/{{WaiverMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.waiver, 2), 2))
      .replace(/{{FlatExtraAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.flatExtra, 2), 2))
      .replace(/{{FlatExtraMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.flatExtra, 2), 2))
      .replace(/{{ModalPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
      .replace(/{{ModalPremiumMonthly}}/, Utils.formatNumber(Utils.round(paymentOptions.monthly.total, 2), 2))
      .replace(/{{ModalAnnualizedPremiumAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.annual.total, 2), 2))
      .replace(
        /{{ModalAnnualizedPremiumMonthly}}/,
        Utils.formatNumber(Utils.round(paymentOptions.monthly.annualTotal, 2), 2)
      );

    if (!!paymentOptions.quarterly && !!paymentOptions.semiannual) {
      body = body
        .replace(
          /{{BasePremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.basePremium, 2), 2)
        )
        .replace(
          /{{BasePremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.basePremium, 2), 2)
        )
        .replace(/{{ModalPremiumSemiAnnual}}/, Utils.formatNumber(Utils.round(paymentOptions.semiannual.total, 2), 2))
        .replace(/{{ModalPremiumQuarterly}}/, Utils.formatNumber(Utils.round(paymentOptions.quarterly.total, 2), 2))
        .replace(
          /{{ModalAnnualizedPremiumSemiAnnual}}/,
          Utils.formatNumber(Utils.round(paymentOptions.semiannual.annualTotal, 2), 2)
        )
        .replace(
          /{{ModalAnnualizedPremiumQuarterly}}/,
          Utils.formatNumber(Utils.round(paymentOptions.quarterly.annualTotal, 2), 2)
        );
    }

    if (illustrationRequest.riders) {
      illustrationRequest.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body.replace(/{{GirFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body.replace(/{{RtrFaceAmount}}/g, Utils.formatNumber(Utils.round(rider.amount, 2), 2));
        }
        if (rider.type === RiderType.ALIR) {
          hasAlir = true;
          body = body.replace(
            /{{AlirFaceAmount}}/g,
            Utils.formatNumber(
              Utils.round(this.basicIllustrationData.illustration.payload.policy.alirAtIssueUnits, 2),
              2
            )
          );
        }
      });
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.annual.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrAnnual}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverAnnual}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
      if (hasAlir) {
        const alirRider = illustrationRequest.riders.find((rider) => rider.type === RiderType.ALIR);
        body = body.replace(/{{AlirAnnual}}/, Utils.formatNumber(Utils.round(alirRider.amount, 2), 2));
        body = body.replace(/{{AlirMonthly}}/, Utils.formatNumber(Utils.round(alirRider.amount / 12, 2), 2));
      }
    }

    if (paymentOptions.annual.riders) {
      paymentOptions.monthly.riders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          body = body
            .replace(/{{GirMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{GirWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
        if (rider.type === RiderType.RTR) {
          body = body
            .replace(/{{RtrMonthly}}/, Utils.formatNumber(Utils.round(rider.premium, 2), 2))
            .replace(/{{RtrWaiverMonthly}}/, Utils.formatNumber(Utils.round(rider.waiver, 2), 2));
        }
      });
    }

    return this.getHtmlContent(style, body);
  }

  getHtmlContent_Supplemental_NS_dividends(illustrationRequest: IllustrationRequest) {
    const startDate = this.basicIllustrationData.illustration.payload.policy.segments[0].startDate;
    const loanInterestRate = this.basicIllustrationData.illustration.payload.policy.loanInterestRate;
    const dividendSection = NS_dividends.DividendsMap[illustrationRequest.dividendOption];
    const style = '';
    const requestRiders = illustrationRequest.riders;
    let requestHasGir = false;
    let requestHasRtr = false;
    let requestHasAlir = false;
    const isNYIllustration: boolean = illustrationRequest.state === 'NY';
    let requestHasCir = false;
    let hasCirRiderAndPUA = false;

    if (requestRiders && requestRiders.length > 0) {
      requestRiders.forEach((rider) => {
        if (rider.type === RiderType.GIR) {
          requestHasGir = true;
        }
        if (rider.type === RiderType.RTR) {
          requestHasRtr = true;
        }
        if (rider.type === RiderType.ALIR) {
          requestHasAlir = true;
        }
        if (rider.type === RiderType.CIR) {
          requestHasCir = true;
          if (rider.puaAddedToBenefitPool) {
            hasCirRiderAndPUA = true;
          }
        }
      });
    }

    let is1035ExchangeToALIR = illustrationRequest.cashValueTransfer > 0;
    //If the cashTranfer value is less than the premium, we wont show the rider
    if (illustrationRequest.cashValueTransfer) {
      requestHasAlir = true;
      if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.ALIR_ONLY) {
        is1035ExchangeToALIR = true;
      } else if (illustrationRequest.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        if (illustrationRequest.transferAmount > 0) {
          is1035ExchangeToALIR = true;
        } else {
          is1035ExchangeToALIR = false;
        }
      }
    }

    let body = `${
      illustrationRequest.dividendInterestAdjustment > 0
        ? StaticContent.INFO_ABOUT_DIVIDENDS_LOWER_DIVIDENDS_CONCEPT
        : StaticContent.INFO_ABOUT_DIVIDENDS
    }
            ${dividendSection}
            ${is1035ExchangeToALIR ? StaticContent.INFO_ABOUT_DIVIDENDS_1035 : ''}
            ${StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS}
            ${illustrationRequest.waiverUnits > 0 ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_WP : ''}
            ${requestHasGir ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_GIR : ''}
            ${requestHasRtr && !isNYIllustration ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_RTR : ''}
            ${requestHasRtr && isNYIllustration ? NYStaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_RTR : ''}
            ${requestHasAlir ? StaticContent.ADDITIONAL_COVERAGE_PROV_BY_RIDERS_ALIR : ''}`;

    body += StaticContent.IMPORTANT_TAX_INFORMATION_HEADER;

    //check if either a)mecStatus has been set already in illustrationRequest determined by Exchange1035 field on quote page
    //or b)mecYear returned from product illustration call is > 0
    let mecStatus;
    let mecYear;
    let supplementalMecStatus;
    let supplementalMecYear;
    if (illustrationRequest.mecStatus === 'Yes') {
      mecStatus = true;
      mecYear = 1;
    } else if (this.basicIllustrationData.illustration.payload.policy.mecYear > 0) {
      mecStatus = true;
      mecYear = this.basicIllustrationData.illustration.payload.policy.mecYear;
    } else {
      mecStatus = false;
    }

    if (this.supplementalIllustrationData.illustration.payload.policy.mecYear > 0) {
      supplementalMecStatus = true;
      supplementalMecYear = this.supplementalIllustrationData.illustration.payload.policy.mecYear;
    } else {
      supplementalMecStatus = false;
    }

    if (mecStatus && supplementalMecStatus) {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_BOTH_MEC;
      body = body.replace(/{{mecYear}}/g, mecYear);
      body = body.replace(/{{supplementalMecYear}}/g, supplementalMecYear);
    } else if (mecStatus && !supplementalMecStatus) {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_TABULAR_MEC;
      body = body.replace(/{{mecYear}}/g, mecYear);
    } else if (!mecStatus && supplementalMecStatus) {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_SUPPLEMENTAL_MEC;
      body = body.replace(/{{supplementalMecYear}}/g, supplementalMecYear);
    } else {
      body += StaticContent.IMPORTANT_TAX_INFORMATION_BOTH_NO_MEC;
    }

    if (requestHasGir) {
      const gir = this.findRiderByType(requestRiders, RiderType.GIR);
      body = body.replace(/{{GirFaceAmount}}/g, Utils.formatCurrency(gir.amount));
    }

    if (requestHasRtr) {
      if (illustrationRequest.dob) {
        body = body.replace(
          /{{RTR_END_YEAR}}/g,
          (95 - CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)).toString()
        );
      }
    }

    let cirPuaInfo = ``;
    if (hasCirRiderAndPUA) {
      cirPuaInfo = StaticContent.CIR_PUA_INFO;
    }

    if (requestHasAlir) {
      body = body.replace(/{{ALIR_PERCENTAGE}}/g, '7.5%');
    }

    body += StaticContent.IMPORTANT_TAX_INFORMATION_SUPPLEMENTAL_FOOTER;

    body += StaticContent.IMPORTANT_POLICY_LOANS_INFORMATION_HEADER;

    body +=
      StaticContent.IMPORTANT_POLICY_SUPPLEMENTAL_LOAN_INTEREST_RATE +
      StaticContent.IMPORTANT_POLICY_LOANS_HYPOTHETICAL_INTEREST_RATE_REQUEST +
      StaticContent.IMPORTANT_POLICY_LOANS_SUPPLEMENTAL_PAYMENT_ASSUMPTION;

    body += StaticContent.IMPORTANT_POLICY_LOANS_SUPPLEMENTAL_FOOTER;

    body = body
      .replace(/{{PRODUCT}}/g, constants.ProductSelection[illustrationRequest.productCode])
      .replace(/{{YEAR}}/, CoverpathUtils.getCurrentRateYear().toString())
      .replace(
        /{{DIVIDEND_ADJUSTMENT}}/,
        CoverpathUtils.decimalToPercent(illustrationRequest.dividendInterestAdjustment).toString()
      )
      .replace(
        /{{DIVIDEND_ADJUSTMENT}}/,
        CoverpathUtils.decimalToPercent(illustrationRequest.dividendInterestAdjustment).toString()
      )
      .replace(/{{RATE_ELECTION}}/, illustrationRequest.loanRateType === 'Fixed' ? 'a fixed' : 'an adjustable')
      .replace(/{{RATE_FIXED}}/, illustrationRequest.loanRateType === 'Fixed' ? 'Loans affect dividends.' : '')
      .replace(/{{HAS_CIR_PUA}}/g, cirPuaInfo)
      .replace(/{{LOAN_INTEREST_RATE}}/g, (loanInterestRate * 100).toFixed(2))
      .replace(/{{STATE}}/g, Assumptions.stateValues[illustrationRequest.state])
      .replace(/{{START_DATE}}/g, Utils.tzDate(new Date(startDate)).toMonthDY())
      .replace(
        /{{VARIABLE_RATE_MESSAGE}}/,
        illustrationRequest.loanRateType.toLowerCase() === 'variable'
          ? ' This rate may change on each policy anniversary date.'
          : ''
      );

    return this.getHtmlContent(style, body);
  }

  getHtmlContent_Supplemental_NS_ColumnHeadingDefinitions(illustrationRequest: IllustrationRequest): string {
    let html = `${columnHeaderDefinitions.title}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.AGE_END_YEAR)}`;

    const hasCirRider = !!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR);
    let cirAddPaidupAdditionsEndYear = ``;
    let cirAddTotalCashValueEndYear = ``;
    let cirAddTotalDeathBenefitEndYear = ``;
    if (hasCirRider) {
      cirAddPaidupAdditionsEndYear = `${columnHeaderDefinitions.CIR_ADD_PAIDUP_ADDITIONS_END_YEAR}`;
      cirAddTotalCashValueEndYear = `${columnHeaderDefinitions.CIR_ADD_TOTAL_CASH_VALUE_END_YEAR}`;
      cirAddTotalDeathBenefitEndYear = `${columnHeaderDefinitions.CIR_ADD_TOTAL_DEATH_BENEFIT_END_YEAR}`;
    }

    switch (illustrationRequest.dividendOption) {
      case 'PUA':
        html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_LOAN)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_NET_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_SURRENDER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.BASIC_POLICY_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CONTRACT_PREMIUM)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.PAIDUP_ADDITIONS_END_YEAR)}`;
        break;
      case 'Cash':
        html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_LOAN)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_NET_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_SURRENDER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.BASIC_POLICY_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CONTRACT_PREMIUM)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.DIVIDEND_PAID_CASH_BEG_YAER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR)}`;
        break;
      case 'PRPUA':
        html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_LOAN)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_NET_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_SURRENDER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.BASIC_POLICY_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CONTRACT_PREMIUM)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_PREMIUM_BEG_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.PAIDUP_ADDITIONS_END_YEAR)}`;
        break;
      case 'PRCash':
        html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_LOAN)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_NET_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_SURRENDER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.BASIC_POLICY_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CONTRACT_PREMIUM)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.DIVIDEND_PAID_CASH_BEG_YAER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_PREMIUM_BEG_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR)}`;
        break;
      case 'Accumulate':
        html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_DIV_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_LOAN)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_NET_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_OUTLAY)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.ANNUAL_SURRENDER)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.BASIC_POLICY_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CASH_VALUE_ACCUMS_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.CONTRACT_PREMIUM)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_CASH_VAL_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.GUARANTEED_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_CASH_VALUE_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.NET_DEATH_BENEFIT_END_YEAR)}
                    ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TAX_ON_DIST_BEG_YEAR)}`;
        break;
    }
    html += `${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_CASH_VALUE_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_CASH_VALUE_OF_ADDITIONS_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_DEATH_BENEFIT_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_OUTSTANDING_LOAN_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_PAID_UP_ADDITIONS_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.TOTAL_PAIDUP_INS_END_YEAR)}
            ${columnHeaderDefinitions.parse(columnHeaderDefinitions.YEAR)}`;

    html = html
      .replace(/{{CirAddPaidupAdditionsEndYear}}/g, cirAddPaidupAdditionsEndYear)
      .replace(/{{CirAddTotalCashValueEndYear}}/g, cirAddTotalCashValueEndYear)
      .replace(/{{CirAddTotalDeathBenefitEndYear}}/g, cirAddTotalDeathBenefitEndYear);

    return html;
  }

  @serverOnly()
  getHTMLContentForPaymentsSupplemental(illustrationRequest: IllustrationRequest): string {
    const styles = IACmain.styles + NS_PaymentOptions.style;

    let htmlContent =
      (StateSpecification.IAC.includes(illustrationRequest.state)
        ? this.getHTMLContent_NS_IAC(illustrationRequest, true)
        : '') +
      StaticContent.KEY_TERMS_USED_IN_ILLUSTRATION +
      this.getHtmlContent_Supplemental_NS_ColumnHeadingDefinitions(illustrationRequest) +
      this.getHtmlContent_NS_Supplemental_PaymentOptions(illustrationRequest);

    let cirTerms = ``;
    if (!!illustrationRequest.riders.find((rider) => rider.type === RiderType.CIR)) {
      cirTerms = `${StaticContent.INITIAL_BASE_BENEFIT_POOL_INFO}`;
    }
    htmlContent = htmlContent.replace(/{{InitialBaseBenefitPoolInfo}}/g, cirTerms);
    return this.getHtmlContent(styles, htmlContent);
  }

  getHtmlContent_NS_Supplemental_PaymentOptions(illustrationRequest: IllustrationRequest): string {
    const paymentOptions = this.basicIllustrationAdditionalData.paymentOptions;
    let paymentOptionsHTML = StateSpecification.NoPremiumPaymentOption.includes(illustrationRequest.state)
      ? ''
      : NS_PaymentOptions.supplementalContent
          .replace(/{{AnnualPremium}}/g, Utils.formatCurrency(paymentOptions.annual.total))
          .replace(/{{MonthlyPremium}}/, Utils.formatCurrency(paymentOptions.monthly.total))
          .replace(/{{TotalMonthlyPremium}}/, Utils.formatCurrency(paymentOptions.monthly.annualTotal))
          .replace(/{{AdditionalChargeMonthly}}/, Utils.formatCurrency(paymentOptions.monthly.additionalCharge))
          .replace(/{{RATE}}/g, '9.5%'); // Rate is 9.5%
    paymentOptionsHTML += StaticContent.CONSIDER_ADDITONAL_COVERAGE.replace(
      /{{PRODUCT}}/g,
      constants.ProductSelection[illustrationRequest.productCode]
    );
    return this.getHtmlContent(NS_PaymentOptions.style, paymentOptionsHTML);
  }

  // 'Known' input will be dynamic once premium solve functionality is added
  getClientInputSummary(illustrationRequest: IllustrationRequest): string {
    let hasRTR = false;
    let hasGIR = false;
    let hasALIRPlanned = false;
    const hasALIRScheduled = false;
    illustrationRequest.riders.forEach((rider) => {
      if (rider.type === RiderType.RTR) {
        hasRTR = true;
      }
      if (rider.type === RiderType.GIR) {
        hasGIR = true;
      }
      if (rider.type === RiderType.ALIR) {
        hasALIRPlanned = true;
      }
    });
    const rateClass = RateClasses.findByStringCode(illustrationRequest.rateClass);
    const tableRating = RateClasses.isTableRated(rateClass.code)
      ? CPPolicyRateClassDescriptions[illustrationRequest.rateClass].shortDescription
      : 'None';
    const classDesc = RateClasses.isTableRated(rateClass.code)
      ? CPPolicyRateClassDescriptions[illustrationRequest.rateClass].tobaccoDescription
      : RateClasses[illustrationRequest.rateClass].desc;

    return `<div>
    <hr class="footerbreak"/>
    <table class="full-width">
      <tr>
        <td><p class="p-left">Company: MMD</p></td>
        <td><p class="p-right">${this.getDateInPrettyFormat()}</p></td>
      </tr>
      <tr>
        <td><p class="p-left">Product: ${
          Product.productTypes.Whole.productNames[illustrationRequest.productCode]
        }</p></td>
      </tr>
    </table>
    <hr class="footerbreak"/>

    <h1>
      <div>Insured</div>
      <hr class="supplemental-illustration-input-summary-break"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">First Name</p></td>
        <td><p class="p-right">${illustrationRequest.customerPerson.firstName}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Last Name</p></td>
        <td><p class="p-right">${illustrationRequest.customerPerson.lastName}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Sex</p></td>
        <td><p class="p-right">${illustrationRequest.gender === '1' ? 'Male' : 'Female'}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Issue Age</p></td>
        <td><p class="p-right">${CoverpathUtils.getInsuranceAge(
          illustrationRequest.dob,
          illustrationRequest.startDate
        )}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Class</p></td>
        <td><p class="p-right">${classDesc}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Table Rating</p></td>
        <td><p class="p-right">${tableRating}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Tax Bracket Percent</p></td>
        <td><p class="p-right">${illustrationRequest.taxRate * 100}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Contract State</p></td>
        <td><p class="p-right">${illustrationRequest.state}</p></td>
      </tr>
    </table>

    <h1>
      <div>Design</div>
      <hr class="supplemental-illustration-input-summary-break"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">Design</p></td>
        <td><p class="p-right">Base</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Premium Mode</p></td>
        <td><p class="p-right">${illustrationRequest.paymentFrequency}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Known</p></td>
        <td><p class="p-right">Face Amount</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Face Amount</p></td>
        <td><p class="p-right">${Utils.formatCurrency(illustrationRequest.faceAmount, 0, false)}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Dividend Option</p></td>
        <td><p class="p-right">${this.dividendOptionMap[illustrationRequest.dividendOption]}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Dividend Rate</p></td>
        <td><p class="p-right">Current${
          illustrationRequest.dividendInterestAdjustment > 0
            ? ` - ${CoverpathUtils.decimalToPercent(illustrationRequest.dividendInterestAdjustment).toString()}%`
            : ''
        }</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Loan Rate</p></td>
        <td><p class="p-right">${illustrationRequest.loanRateType}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">1035 Exchange</p></td>
        <td><p class="p-right">${illustrationRequest.cashValueTransfer > 0 ? 'Y' : 'N'}</p></td>
      </tr>
    </table>

    <h1>
      <div>Options</div>
      <hr class="footerbreak"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">Print to Age/Year/Max</p></td>
        <td><p class="p-right">Max</p></td>
      </tr>
    </table>

    <h1>
      <div>Riders</div>
      <hr class="supplemental-illustration-input-summary-break"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">Waiver of Premium [WP]</p></td>
        <td><p class="p-right">${illustrationRequest.waiverUnits > 0 ? 'Y' : 'N'}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Renewable Term Rider [RTR]</p></td>
        <td><p class="p-right">${hasRTR ? 'Y' : 'N'}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Guaranteed Insurability Rider [GIR]</p></td>
        <td><p class="p-right">${hasGIR ? 'Y' : 'N'}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Additional Life Ins. Rider [ALIR] - Scheduled</p></td>
        <td><p class="p-right">${hasALIRScheduled ? 'Y' : 'N'}</p></td>
      </tr>
    </table>

    <h1>
      <div>Disbursements</div>
      <hr class="supplemental-illustration-input-summary-break"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">Disbursements</p></td>
        <td><p class="p-right">Y</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Disbursement Option</p></td>
        <td><p class="p-right">Switch at Basis</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Disbursements</p></td>
        <td><p class="p-right">${illustrationRequest.startAge} to ${illustrationRequest.endAge} - Solve for Max</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Loan Interest Option</p></td>
        <td><p class="p-right">Borrow from Policy</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Repay Loans</p></td>
        <td><p class="p-right">N</p></td>
      </tr>
    </table>

    <h1>
      <div>Agent Info</div>
      <hr class="supplemental-illustration-input-summary-break"/>
    </h1>
    <table class="full-width">
      <tr>
        <td><p style="text-indent: 10px;">Agent First Name</p></td>
        <td><p class="p-right">${illustrationRequest.agent.firstName}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Agent Last Name</p></td>
        <td><p class="p-right">${illustrationRequest.agent.lastName}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Agent Address1</p></td>
        <td><p class="p-right">${illustrationRequest.agent.address.street}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Agent City</p></td>
        <td><p class="p-right">${illustrationRequest.agent.address.city}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Agent State</p></td>
        <td><p class="p-right">${illustrationRequest.agent.address.state}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Agent Zip Code</p></td>
        <td><p class="p-right">${illustrationRequest.agent.address.zip}</p></td>
      </tr>
      <tr>
        <td><p style="text-indent: 10px;">Bank Sale</p></td>
        <td><p class="p-right">${illustrationRequest.bankSale ? 'Y' : 'N'}</p></td>
      </tr>
    </table>
  </div>`;
  }

  getClientInputSummaryHtmlContent(style: string, body: string) {
    return `
      <head>
        <style>
          ${style}
        </style>
      </head>
      <body>
        ${body}
      </body>`;
  }

  getGenericPolicyName(productCode: string) {
    const term = constants.ProductSelection[productCode];
    switch (productCode) {
      case Product.products.CPL10.code:
      case Product.products.CPL10A.code:
      case Product.products.CPL12.code:
      case Product.products.CPL15.code:
      case Product.products.CPL15A.code:
      case Product.products.CPL20.code:
      case Product.products.CPL20A.code:
        return `Limited Payment Whole Life with Premiums Payable for ${term} Years`;
      case Product.products.CPL65.code:
      case Product.products.CPL65A.code:
        return 'Limited Payment Whole Life with Premiums Payable to Age 65';
      case Product.products.CPL100.code:
      case Product.products.CPL100A.code:
        return 'Whole Life Premiums Payable to Age 100';
    }
  }

  getCirUnderwritingRateClass(underwritingRateClass: string): string {
    let cirRateClass;

    switch (underwritingRateClass) {
      case 'Ultra Preferred Non-Tobacco':
      case 'Select Preferred Non-Tobacco':
      case 'Non-Tobacco':
        cirRateClass = 'Non-Tobacco';
        break;

      case 'Select Preferred Tobacco':
      case 'Tobacco':
        cirRateClass = 'Tobacco';
        break;
      default:
        cirRateClass = '';
        break;
    }

    return cirRateClass;
  }

  getHtmlContent_CirClaimIllustrationTabularData(cirClaimIllustrationResponse, pageNumber): string {
    const rowsOnOtherPages = constants.illustrationRowsOnOtherPages;
    let body: string = '';

    const startIndex = pageNumber > 0 ? rowsOnOtherPages * (pageNumber - 1) : 0;
    body += `${CirClaimIllustration.getCirClaimIllustration(
      cirClaimIllustrationResponse,
      startIndex,
      rowsOnOtherPages
    )}`;

    const style: string = `${IllustrationSummary.getStyles()}`;

    return this.getHtmlContent(style, body);
  }
}
